import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  ArrowDown,
  Loader2,
  Compass as CompassIcon,
  MessageSquareQuote,
  X,
  Check,
  CheckCircle2,
  Circle,
  GripVertical,
  ListChecks,
  Maximize2,
  StickyNote,
  Target,
  PenLine,
} from "lucide-react";
import { startPreview, getSession, interact, getNoticing, otStart, feedbackEvent, askCompass, scControl, roundChoice, beginCompositionBridge, advanceCompositionBridge, getCompositionBridge, cascadeReturn, cascadeResume, getResumeStatus, cMyWritingPathSession, cStrengthenStart, writingPathExit, cStandaloneStart, cStandaloneResume, cStandaloneImport } from "@/lib/api";
import StandaloneWritingPathEntry from "@/components/StandaloneWritingPathEntry";
import StrengthenWritingEntry from "@/components/StrengthenWritingEntry";
import { flushAll } from "@/lib/autosave";
import { toast } from "sonner";
import { getTestMode } from "@/lib/testMode";
import { metacognitionSequence } from "@/lib/writerMetacognition";
import { findThesisRanges, snapRangesToSentences, subtractRanges, locateUnit } from "@/lib/thesisMatch";

// Consistent student-facing Back control (NAVIGATION only — never reverses developmental/controller state).
const BackControl = ({ onClick, className = "" }) => (
  <button
    type="button"
    data-testid="back-control"
    onClick={onClick}
    aria-label="Back"
    className={`inline-flex items-center gap-1.5 text-[13px] font-medium text-stone-500 hover:text-stone-900 transition-colors ${className}`}
  >
    <ArrowLeft className="h-4 w-4" /> Back
  </button>
);

import ExperienceReflection from "@/components/ExperienceReflection";
import TeacherReflection from "@/components/TeacherReflection";
import EpisodeReview from "@/components/EpisodeReview";
import ThesisFirstShell from "@/components/ThesisFirstShell";
import ParagraphMap from "@/components/ParagraphMap";
import CompositionProgress from "@/components/CompositionProgress"; // eslint-disable-line no-unused-vars -- kept for opt-in (Composition minimalism hides it)
import ScratchSpace from "@/components/ScratchSpace";
import ThinkingWorkspace from "@/components/ThinkingWorkspace";
import CascadeOrientation from "@/components/CascadeOrientation";
import FunctionalOrganization from "@/components/FunctionalOrganization";
import FunctionallyScaffoldedComposition from "@/components/FunctionallyScaffoldedComposition";
import EssayComposition from "@/components/EssayComposition";
// FO-UNIFICATION (P4): the separate Functional Organization ROOM is bypassed — organizing thought and
// organizing-thought-as-an-essay-for-a-reader both happen inside the OT living workspace, then Composition.
// This one reversible flag restores the old OT → FO → Composition flow (FunctionalOrganization kept dormant).
const FO_INLINE = true;
import EssayOrientation from "@/components/EssayOrientation";
import OTThinkingPanel from "@/components/OTThinkingPanel";
import SentenceCraftProgress from "@/components/SentenceCraftProgress";
import SentenceCraftCompletion from "@/components/SentenceCraftCompletion";
import StudentEntry from "@/components/StudentEntry";
import WelcomeScreen from "@/components/WelcomeScreen";
import WelcomeIntro from "@/components/feedback/WelcomeIntro";
import FeedbackModal from "@/components/feedback/FeedbackModal";
import InContextFeedback from "@/components/feedback/InContextFeedback";
import EarlyExitModal from "@/components/feedback/EarlyExitModal";
import { renderEmphasis, renderCoaching, renderCoachingRich } from "@/lib/formatText";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { StageStepper, SessionRoadmap, NextAction } from "@/components/SessionNav";
import OrientationLayer from "@/components/OrientationLayer";
import { difficultyCheck } from "@/lib/orientation";
import { useAuth } from "@/context/AuthContext";
import AccountMenu from "@/components/AccountMenu";
import AuthModal from "@/components/AuthModal";
import MyWork from "@/components/MyWork";
import { claimSession } from "@/lib/api";
import { essayResumeComposition } from "@/lib/api";  // PHASE C3

// Experience The Writing Studio public flow: Welcome (Ch3) -> Assignment (Ch4) -> Writing
// (Ch4 minimal interim) -> coaching -> reflection. The educator temporarily
// becomes the learner: they create an authentic assignment, then respond to it.
// Canonical v2 is the DEFAULT instructional architecture for all preview routes.
// Legacy (consolidated_v2) is retained only as a rollback: append ?legacy=1 to the
// URL to request it. WANT_CANONICAL drives both session creation and resume matching.
const WANT_CANONICAL = !new URLSearchParams(window.location.search).has("legacy");

// Canonical instructional orientation: CURRENT INSTRUCTIONAL FOCUS + vertical Writing Structure.
// All state is derived ONLY from authoritative backend fields (focus_of_work / established_structures).
const CANONICAL_STRUCTURES = ["Opening", "Thesis", "Elaboration", "Evidence / Example", "Conclusion"];

// Sprint 1.4 — each structural element displays the communicative FUNCTION it serves,
// so learners see that structures exist because they perform communicative work.
const FUNCTION_QUESTIONS = {
  "Opening": "What does the reader need before they can understand my thesis?",
  "Thesis": "What thesis do I want my reader to construct?",
  "Elaboration": "What about this thesis does my reader still need to understand?",
  "Evidence / Example": "What would convince my reader that this thesis is warranted?",
  "Conclusion": "What integrated understanding should my reader leave with?",
};

// The learner-facing communicative DIAGRAM (Thesis ▼ Elaboration + question) is frozen and hidden
// per product decision — the highlighting + instructional dialogue carry the pedagogy. The backend
// spans, highlighting, and relation/question architecture are all kept. Devs can re-enable the
// diagram with ?diagram in the URL (no permanent visualization until use shows one is needed).
const SHOW_COMMUNICATIVE_DIAGRAM =
  typeof window !== "undefined" && new URLSearchParams(window.location.search).has("diagram");
// TWO-ROUND bounded experience — enabled explicitly (e.g. ?preview=writing&two_round). Keeps the
// default single-flow writing preview unchanged for existing tests. The OT→Old-Composition testing
// bridge (below) also turns this on for its session, so the two-pane workspace + downstream render.
const TWO_ROUND_URL =
  typeof window !== "undefined" &&
  (new URLSearchParams(window.location.search).has("two_round") ||
   new URLSearchParams(window.location.search).has("experience"));

const OrientationMarker = ({ state }) => {
  if (state === "established")
    return <span aria-label="Established" className="text-emerald-700 font-bold w-3 inline-block text-center">✓</span>;
  if (state === "current")
    return <span aria-label="Current focus" className="text-[#8C3A2A] font-bold w-3 inline-block text-center">●</span>;
  return <span aria-label="Not currently in focus" className="text-stone-300 w-3 inline-block text-center">○</span>;
};


// The Writing Studio 4.10 — SENTENCE CRAFT learner orientation. After a paragraph is structurally ready, the
// work shifts to HOW each sentence communicates. This shows the ONE current sentence-level focus in
// learner-facing language (never internal operation names) and keeps the thesis in view so the
// learner reads each sentence in relation to the whole.
// PHENOMENON-FIRST card — teaches the WRITING ISSUE (name / what it is / why it matters / general fix /
// how many times it occurs) BEFORE the specific sentence. Deterministic payload from the SC engine.
const PhenomenonCard = ({ card }) => {
  if (!card) return null;
  const total = Number(card.instances_found) || 0;
  const cur = Number(card.current_instance) || 0;
  return (
    <div data-testid="sc-phenomenon-card" className="border border-[#8C3A2A]/30 bg-white rounded-sm px-3.5 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#8C3A2A] font-mono-panel">Issue</div>
        {total > 1 && (
          <div className="text-[10px] uppercase tracking-[0.16em] text-stone-400 font-mono-panel" data-testid="sc-phenomenon-count">
            Instance {cur} of {total}
          </div>
        )}
      </div>
      <div data-testid="sc-phenomenon-name" className="mt-0.5 text-[16px] font-serif-display text-[#8C3A2A] font-bold leading-snug">
        {card.issue_name}
      </div>
      <dl className="mt-2 space-y-1.5">
        {card.what_it_is && (
          <div data-testid="sc-phenomenon-what">
            <dt className="text-[9.5px] uppercase tracking-[0.16em] text-stone-400 font-mono-panel">What it is</dt>
            <dd className="text-[13px] text-stone-800 leading-snug">{card.what_it_is}</dd>
          </div>
        )}
        {card.why_it_matters && (
          <div data-testid="sc-phenomenon-why">
            <dt className="text-[9.5px] uppercase tracking-[0.16em] text-stone-400 font-mono-panel">Why it matters</dt>
            <dd className="text-[13px] text-stone-800 leading-snug">{card.why_it_matters}</dd>
          </div>
        )}
        {card.general_fix && (
          <div data-testid="sc-phenomenon-fix">
            <dt className="text-[9.5px] uppercase tracking-[0.16em] text-stone-400 font-mono-panel">General fix</dt>
            <dd className="text-[13px] text-stone-800 leading-snug">{card.general_fix}</dd>
          </div>
        )}
      </dl>
      {total > 1 && (
        <div className="mt-2 flex items-center gap-1.5" aria-hidden data-testid="sc-phenomenon-dots">
          {Array.from({ length: Math.min(total, 8) }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 rounded-full ${i < cur - 1 ? "bg-emerald-500" : i === cur - 1 ? "bg-[#8C3A2A]" : "bg-stone-200"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};


const SentenceCraftOrientation = ({ focusLabel, thesis, routeUpward }) => {
  return (
    <div data-testid="sentence-craft-orientation" className="mb-4 space-y-3">
      <div
        data-testid="preview-sc-focus"
        className="border border-[#e0c4bd] bg-[#fbf5f3] rounded-sm px-3 py-2"
      >
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] font-mono-panel">
          Strengthening Sentence by Sentence
        </div>
        <div
          data-testid="preview-sc-focus-label"
          className="mt-0.5 text-[15px] font-serif-display text-[#8C3A2A] font-bold leading-snug"
        >
          {focusLabel || "Working on this sentence"}
        </div>
        <div className="text-[12px] text-stone-500 mt-1 leading-snug">
          {routeUpward
            ? "This sentence may belong somewhere else — let's decide where it fits before polishing anything."
            : "Your paragraph is set. Now we shape one sentence at a time so a reader gets exactly what you mean."}
        </div>
      </div>
      {thesis && (
        <div
          data-testid="preview-sc-thesis"
          className="border border-stone-200 bg-stone-50 rounded-sm px-3 py-2"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-mono-panel">
            Your main point (keep this in view)
          </div>
          <div className="text-[13px] text-stone-700 mt-1 leading-snug italic font-serif-display">
            {thesis}
          </div>
        </div>
      )}
    </div>
  );
};


const CanonicalOrientation = ({ focus, description, thesis, thesisVerbatim, established, operation }) => {
  const est = new Set(established || []);
  // 4.9.1 — the learner-facing focus reflects the ACTUAL instructional operation, not the canonical
  // paragraph stage. For structural selection / condensation, the work is choosing what the paragraph
  // should carry — not elaborating. (Only this operation case is remapped for now.)
  const isReduction = ["structural_selection", "condense_and_integrate"].includes(
    (operation || "").toLowerCase()
  );
  // On a reduction turn no single canonical stage is the "current" step (the work cuts across the
  // paragraph), so the structure map is a neutral reference rather than pinning an elaboration step.
  const stateOf = (name) =>
    (!isReduction && name === focus ? "current" : est.has(name) ? "established" : "idle");
  const focusLabel = isReduction ? "FOCUSING YOUR PARAGRAPH" : focus;
  const focusQuestion = isReduction
    ? "Your ideas are strong. The work now is deciding what this one paragraph should carry."
    : FUNCTION_QUESTIONS[focus];
  const Row = ({ name, indented }) => {
    const s = stateOf(name);
    const slug = name.replace(/[^a-z]+/gi, "-").toLowerCase();
    const fn = isReduction ? null : FUNCTION_QUESTIONS[name];
    return (
      <div
        data-testid={`writing-structure-row-${slug}`}
        aria-current={s === "current" ? "step" : undefined}
        className={`flex items-baseline gap-2 py-1 ${indented ? "ml-4" : ""}`}
      >
        {indented && <span aria-hidden="true" className="text-stone-300 select-none self-center">└──</span>}
        <OrientationMarker state={s} />
        <span
          className={`text-[12px] shrink-0 ${
            s === "current" ? "font-bold text-stone-900" : s === "established" ? "text-stone-600" : "text-stone-400"
          }`}
        >
          {name}
        </span>
        {fn && (
          <>
            <span aria-hidden="true" className="text-stone-400 shrink-0 select-none">→</span>
            <span
              data-testid={`writing-structure-function-${slug}`}
              className={`text-[12px] leading-snug ${
                s === "current" ? "text-stone-900" : s === "established" ? "text-stone-600" : "text-stone-500"
              }`}
            >
              {fn}
            </span>
          </>
        )}
        {s === "current" && (
          <span className="ml-1 text-[9px] uppercase tracking-wider text-[#8C3A2A] border border-[#e0c4bd] rounded-sm px-1 py-px shrink-0 self-center" data-testid={`writing-structure-status-${slug}`}>
            developing
          </span>
        )}
        {s === "established" && (
          <span className="ml-1 text-[9px] uppercase tracking-wider text-stone-500 border border-stone-200 rounded-sm px-1 py-px shrink-0 self-center" data-testid={`writing-structure-status-${slug}`}>
            present
          </span>
        )}
      </div>
    );
  };
  return (
    <div data-testid="canonical-orientation" className="mb-4 space-y-3">
      <div
        data-testid="preview-focus-of-work"
        className="border border-stone-300 bg-stone-50 rounded-sm px-3 py-2"
      >
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-mono-panel">
          Current Instructional Focus
        </div>
        <div
          data-testid="preview-focus-of-work-structure"
          className="mt-0.5 flex items-baseline gap-2 flex-wrap"
        >
          <span className="text-[15px] font-serif-display text-[#8C3A2A] font-bold uppercase tracking-wide shrink-0">
            {focusLabel}
          </span>
          {focusQuestion && (
            <>
              <span aria-hidden="true" className="text-stone-400 shrink-0 select-none">→</span>
              <span data-testid="preview-focus-function" className="text-[13px] text-stone-800 leading-snug">
                {focusQuestion}
              </span>
            </>
          )}
        </div>
        {!isReduction && description && (
          <div className="text-[12px] text-stone-500 mt-1 leading-snug">{description}</div>
        )}
      </div>
      {thesis && (
        <div
          data-testid="preview-your-thesis"
          className="border border-[#e0c4bd] bg-[#fbf5f3] rounded-sm px-3 py-2"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] font-mono-panel">
            {thesisVerbatim ? "Your Thesis" : "The Writing Studio's current reading of your thesis"}
          </div>
          <div
            data-testid="preview-your-thesis-text"
            className="text-[13px] text-stone-800 mt-1 leading-snug italic font-serif-display"
          >
            {thesisVerbatim ? <>&ldquo;{thesis}&rdquo;</> : thesis}
          </div>
        </div>
      )}
      <div data-testid="writing-structure-map" className="border border-stone-200 rounded-sm px-3 py-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-mono-panel mb-1">
          Writing Structure
        </div>
        <Row name="Opening" />
        <Row name="Thesis" />
        <div aria-hidden="true" className="ml-[3px] text-stone-400 text-[14px] leading-none" title="Thesis and Elaboration develop each other">⇅</div>
        <Row name="Elaboration" />
        <Row name="Evidence / Example" indented />
        <Row name="Conclusion" />
        <div className="mt-2 pt-1.5 border-t border-stone-100 text-[10px] text-stone-400 flex flex-wrap gap-x-3 gap-y-0.5">
          <span><span className="text-emerald-700 font-bold">✓</span> Established</span>
          <span><span className="text-[#8C3A2A] font-bold">●</span> Current</span>
          <span><span className="text-stone-300">○</span> Not in focus</span>
        </div>
      </div>
    </div>
  );
};

// Visible Interpretation overlay (MVP, grayscale-safe). Renders the draft as transparent
// text behind the editable textarea with a STABLE visual grammar layered ON TOP of the
// student's paragraph, which always stays one continuous piece of writing:
//   • boxed label + subtle shading (THESIS)      = the whole Thesis unit
//   • boxed label + subtle shading (ELABORATION) = the whole Elaboration unit
//   • dotted underline                            = the local passage in current instructional focus
//   • no marking                                  = text outside the current interpretation
// Each region renders as ONE inline element (so its label appears once and text never breaks
// into sections); the focus portion is an inner dotted-underlined span within its region.
function renderInterpretationSegments(text, regions, portionRanges, focusClass = "vi-focus-elab") {
  if (!text) return text;
  const n = text.length;
  const inPortion = new Array(n).fill(false);
  (portionRanges || []).forEach(([s, e]) => {
    for (let i = Math.max(0, s); i < Math.min(n, e); i++) inPortion[i] = true;
  });
  const isWs = (ch) => /\s/.test(ch);
  // render a slice of text; dotted-underline the focus-portion runs but NEVER underline
  // whitespace (so the underline never shows over blank space or at a line wrap).
  const renderWithUnderline = (start, end, keyPrefix) => {
    const parts = [];
    let i = start;
    while (i < end) {
      const u = inPortion[i];
      let j = i + 1;
      while (j < end && inPortion[j] === u) j++;
      if (u) {
        // split the focus run so whitespace sub-runs stay unmarked
        let k = i;
        while (k < j) {
          const ws = isWs(text[k]);
          let m = k + 1;
          while (m < j && isWs(text[m]) === ws) m++;
          const chunk = text.slice(k, m);
          if (ws) {
            parts.push(chunk);
          } else {
            parts.push(
              <span key={`${keyPrefix}-u-${k}`} data-testid="vi-focus-marker" className={`vi-focus-marker ${focusClass}`}>
                {chunk}
              </span>
            );
          }
          k = m;
        }
      } else {
        parts.push(text.slice(i, j));
      }
      i = j;
    }
    return parts;
  };
  const sorted = [...(regions || [])].filter((r) => r.end > r.start).sort((a, b) => a.start - b.start);
  const out = [];
  let cursor = 0;
  sorted.forEach((r, idx) => {
    if (r.start > cursor) out.push(renderWithUnderline(cursor, r.start, `pre-${idx}`));
    out.push(
      <mark
        key={`region-${idx}`}
        data-testid={r.testid}
        data-label={r.label}
        className={`${r.className} text-transparent`}
      >
        {renderWithUnderline(r.start, r.end, `reg-${idx}`)}
      </mark>
    );
    cursor = r.end;
  });
  if (cursor < n) out.push(renderWithUnderline(cursor, n, "tail"));
  return out;
}

export default function PublicPreview({ mode = "ot" }) {
  const [session, setSession] = useState(null);
  const [studioOverride, setStudioOverride] = useState(false);   // Thesis-First shell → full studio (quiet secondary access)
  const [assignment, setAssignment] = useState("");   // Ch4 — the educator's authentic assignment (authoritative task)
  const [response, setResponse] = useState("");        // Ch4 — the one-paragraph response written on the Writing Screen
  const [draft, setDraft] = useState("");
  // ZERO-ISSUE SELF-REVIEW — learner-controlled mechanics review when The Writing Studio found no proofreading
  // issues. Purely client-driven (no LLM); selfReviewText holds the learner's editable paragraph.
  const [selfReview, setSelfReview] = useState(false);
  const [selfReviewText, setSelfReviewText] = useState("");
  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [openCoachingId, setOpenCoachingId] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState("");
  // Ask The Writing Studio — persistent learner-to-The Writing Studio dialogue (talk ABOUT the instruction, not a revision).
  const [askMsg, setAskMsg] = useState("");
  // ORIENTATION — proactive difficulty (minimal beta): a bare "I don't know"-type dialogue send asks what's in the way.
  const [difficulty, setDifficulty] = useState(null);
  const diffMemRef = useRef({});
  const [viewStage, setViewStage] = useState(null);  // backward navigation: a completed stage being viewed/edited (null = live)
  const [focusPassageOpen, setFocusPassageOpen] = useState(false);  // isolated pop-up editor for the active sentence
  const [focusPos, setFocusPos] = useState(null);  // {x,y} draggable position (null = default)
  const [scratchOpen, setScratchOpen] = useState(false);  // scratch panel toggle (icon in action bar)
  const focusSeenRef = useRef(null);
  // Chapter 3 — Welcome gate. Shows before the Assignment screen on first entry
  // this visit; "Try another paragraph" (restart) does NOT re-show it.
  const [entered, setEntered] = useState(false);
  // Chapter 4 — once the educator confirms the assignment, advance to the Writing screen.
  const [writingStarted, setWritingStarted] = useState(false);
  // Organizing Thought (Phase 2) — the five-object prewriting flow, before Writing.
  const [otPhase, setOtPhase] = useState(false);
  const [foPhase, setFoPhase] = useState(false);  // Phase 2 Checkpoint 1 — FO teaching stage (OT -> FO -> Composition)
  const [returnConfirmation, setReturnConfirmation] = useState("");  // Checkpoint 2 — developmental confirmation on step-back/resume
  const [returnActive, setReturnActive] = useState(false);           // a cascade return contract is open (routing hint)
  const [recursionPurpose, setRecursionPurpose] = useState(null);  // PHASE C3 — OT return purpose during a Composition→OT recursion
  const [otData, setOtData] = useState(null);
  // OT PREVIEW ONLY: after Organizing Thinking, the SAME session flows directly into Functionally
  // Scaffolded Composition (real OT state, no Frankl fixture, no re-entry). Production Composition
  // handoff is unchanged; ?preview=fsc remains an isolated dev fixture.
  const [fscMode, setFscMode] = useState(false);
  // FULL-PATH CONVERGENCE MARKER — when the student hands a functionally-complete FSC draft into the
  // shared Composition engine, show a brief "You've reached Composition" transition ONCE (full path only).
  // Composition-Only students enter Composition directly and never see this transition.
  const [compositionArrival, setCompositionArrival] = useState(false);
  const [essayMode, setEssayMode] = useState(false);       // essay-level controller (whole-first) active
  const [essayDraft, setEssayDraft] = useState("");
  const [essayReviewed, setEssayReviewed] = useState(false); // whole-first review already happened this session
  // TESTING BRIDGE — OT/FO → OLD-COMPOSITION functional paragraph teaching (see recovery audit). When active,
  // the session runs the existing functional_v3 two-round engine (Round 1 → Sentence Craft → Proofreading →
  // Review) one paragraph at a time. bridgeCursor = {index,total,paragraphs,more,...}. Removable shim.
  const [bridgedComposition, setBridgedComposition] = useState(false);
  const [bridgeCursor, setBridgeCursor] = useState(null);
  // Post-experience: the teacher chooses to review their own experience.
  const [reviewingAsTeacher, setReviewingAsTeacher] = useState(false);
  const [showEpisodeReview, setShowEpisodeReview] = useState(false);  // two-round cumulative Teacher Review
  const [r1ControlDismissed, setR1ControlDismissed] = useState(false);  // learner collapsed the Round-1 finish control
  const [keepRefining, setKeepRefining] = useState(false);  // learner chose to keep refining after Composition reached SC-readiness
  const [r1ContractSeen, setR1ContractSeen] = useState(false);  // two-round Round-1 instructional contract gate
  const [introScreen, setIntroScreen] = useState(1);            // 3-screen intro: Problem -> Difference -> Experience
  // Chapter 6 — Pedagogical Noticing: the "I understand you" beats shown before
  // the frozen engine's developmental response, on the FIRST encounter only.
  const [noticing, setNoticing] = useState(null); // {observations: [obs1, obs2?]}
  const [revealStage, setRevealStage] = useState(0); // 0 none · 1 first observation · 2 second observation
  const submitAtRef = useRef(0); // when the learner submitted (to pace the first observation ~3-5s)
  // Immediate submission acknowledgment ("✓ Revision received") shown within a fraction of a second
  // while the engine works. Set synchronously on submit; only rendered inside the busy/thinking block.
  const [pendingAck, setPendingAck] = useState(null);
  // The Writing Studio Developmental Feedback System.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [earlyExitOpen, setEarlyExitOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [roadmapSeen, setRoadmapSeen] = useState(false);   // session roadmap shown once per new session
  const [stageTransition, setStageTransition] = useState(null);   // interstitial: {done, next, desc, choice}

  // --- Accounts (optional) — sign in to save work; "My Work" library. ---
  const { isAuthed, user } = useAuth();
  const [showMyWork, setShowMyWork] = useState(false);
  const [savePromptOpen, setSavePromptOpen] = useState(false);

  // When the learner signs in mid-session, attach the current anonymous session to
  // their account so their work is saved.
  const handleAuthSuccess = useCallback(async () => {
    if (session?.id) {
      try { await claimSession(session.id); } catch (e) { /* already owned / anon */ }
    }
    toast.success("Signed in — your work is now being saved.");
  }, [session?.id]);

  // Load a saved session from "My Work" into the active workspace (resume in-progress).
  const resumeSessionById = useCallback(async (id) => {
    try {
      let s = await getSession(id);
      if (!s) { toast.error("Could not open that session."); return; }
      setShowMyWork(false);
      setShowEpisodeReview(false);
      // a Writing-Path essay without a classroom assignment becomes the current one (the others are set aside, kept)
      if (s.writing_path && !s.assignment_id) { try { await cStandaloneResume(s.id); } catch (e) { /* still opens; just not pinned as current */ } }
      // an old (pre-Writing-Path) essay: adopted into the Writing Path with its writing kept — the previous Studio never reopens for a student
      if (!s.writing_path && mode !== "teacher" && user) { try { await cStandaloneImport(s.id); s = await getSession(s.id); } catch (e) { /* falls through unchanged */ } }
      setSession(s);
      setAssignment(s.assignment || "");
      if (s.ot) setOtData(s.ot);
      const students = (s.turns || []).filter((t) => t.role === "student" && !t.unit_id);
      if (students.length) setDraft(students[students.length - 1].content || "");
      setWritingStarted(true);
      try { localStorage.setItem("compass_student_session", JSON.stringify({ id: s.id, mode })); } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error("Could not open that session.");
    }
  }, [mode, user]);

  // Open a finished session straight to its Final Review.
  const openReviewById = useCallback(async (id) => {
    try {
      const s = await getSession(id);
      if (!s) { toast.error("Could not open that session."); return; }
      setShowMyWork(false);
      setSession(s);
      setAssignment(s.assignment || "");
      setWritingStarted(true);
      setShowEpisodeReview(true);
    } catch (e) {
      toast.error("Could not open that session.");
    }
  }, []);

  const isProcessing = !!session?.turns?.some((t) => t.status === "processing" || t.status === "streaming");
  const busy = starting || sending || isProcessing;
  // RELIABILITY: latest AI turn finalized as 'failed' (an interrupted/orphaned turn healed by the server).
  // Surfaced as a clear, non-technical retry affordance so the learner is never stuck on "Still working".
  const lastAiTurn = [...(session?.turns || [])].reverse().find((t) => t.role === "ai");
  const lastTurnFailed = lastAiTurn?.status === "failed";

  const allTurns = session?.turns || [];
  // UDWW local-composition transcript turns (unit_id) belong to the unified workspace, never to the legacy composition stream
  const studentTurns = allTurns.filter((t) => t.role === "student" && !t.unit_id);
  // Ask-The Writing Studio dialogue turns are supplemental — kept OUT of the coaching/revision stream so they
  // never become the active coaching card and never affect revision counting.
  const dialogueTurns = allTurns.filter((t) => t.kind === "dialogue");
  const completedAi = allTurns.filter((t) => t.role === "ai" && t.status === "complete" && t.content && t.kind !== "dialogue");
  // WPI state-coherence: after a restoration re-baseline, coaching produced for the ABANDONED questioned
  // version must not remain the CURRENT card (even if it finished streaming after restoration). Only
  // coaching positioned AFTER the newest restoration turn is current; until the next ordinary turn produces
  // it, there is no current card (intentionally invalidated). No restoration turn => unchanged behavior.
  const lastRestorationIdx = (() => { let idx = -1; allTurns.forEach((t, i) => { if (t.wpi_restoration) idx = i; }); return idx; })();
  const activeCoaching = (() => {
    const eligible = lastRestorationIdx < 0
      ? completedAi
      : completedAi.filter((t) => allTurns.indexOf(t) > lastRestorationIdx);
    return eligible.length ? eligible[eligible.length - 1] : null;
  })();
  // STREAMING: the placeholder coaching turn while it is being written (plain text, grows over time).
  // Rendered live as a typewriter so the learner watches the response appear instead of a long spinner.
  const streamingCoaching = allTurns.filter(
    (t) => t.role === "ai" && t.status === "streaming" && (t.content || "").trim() && t.kind !== "dialogue"
  ).slice(-1)[0] || null;
  // UNIFIED COMPASS CONVERSATION — one continuous instructional dialogue. Engine-produced coaching turns
  // AND Ask-The Writing Studio dialogue turns render as chronological bubbles in ONE thread (no separate cards).
  // Purely a PRESENTATION assembly from turns already persisted — adds no LLM call and does not change
  // how coaching is generated or sequenced.
  // Signature that changes whenever ANY turn's status OR content length changes (not merely the count),
  // so a coaching turn finishing (pending/streaming -> complete) re-renders the thread immediately —
  // this is the fix for "I had to refresh to see the new Sentence Craft feedback".
  const convSig = (allTurns || []).map((t) => `${t.id}:${t.status}:${(t.content || "").length}`).join("|");
  const conversation = useMemo(() => {
    const list = [];
    (allTurns || []).forEach((t) => {
      const content = (t.content || "").trim();
      if (!content) return;
      if (t.role === "ai") {
        if (t.status === "complete" && t.kind !== "pending") list.push({ id: t.id, who: "compass", text: content });
      } else if ((t.role === "student" || t.role === "user") && ["dialogue", "answer", "explain"].includes(t.kind)) {
        if (content === "Can you say a little more about what you mean?") return; // legacy Explain-More preset
        list.push({ id: t.id, who: "student", text: content });
      }
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convSig]);
  const convScrollRef = useRef(null);
  // Newest The Writing Studio turn — used to alert the learner ("New response" divider + highlight) and to keep the
  // thread pinned to the bottom so new coaching appears at the bottom without the learner scrolling.
  const latestCompassId = (() => { for (let i = conversation.length - 1; i >= 0; i--) if (conversation[i].who === "compass") return conversation[i].id; return null; })();
  const [newCompassId, setNewCompassId] = useState(null);
  const prevCompassIdRef = useRef(null);
  useEffect(() => {
    if (!latestCompassId) return;
    const prev = prevCompassIdRef.current;
    prevCompassIdRef.current = latestCompassId;
    if (prev !== null && prev !== latestCompassId) {
      setNewCompassId(latestCompassId);
      const t = setTimeout(() => setNewCompassId(null), 6000);
      return () => clearTimeout(t);
    }
  }, [latestCompassId]);
  // CHAT-VIEWPORT AUTO-SCROLL. The dialogue is a bounded, internally-scrolling container (see the
  // sticky compass-column + lg:h-full box below). We scroll THAT container — never the document.
  // "Stick to bottom" is preserved unless the learner has intentionally scrolled up to reread history.
  const autoStickRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const onConvScroll = useCallback(() => {
    const el = convScrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = dist < 80;
    autoStickRef.current = nearBottom;
    if (nearBottom) setShowJump(false);
  }, []);
  const scrollConvToBottom = useCallback(() => {
    const el = convScrollRef.current;
    if (!el) return;
    autoStickRef.current = true;
    el.scrollTop = el.scrollHeight;
    setShowJump(false);
  }, []);
  // Scroll the CONTAINER after the new DOM has painted (rAF), so scrollHeight reflects the new content.
  useLayoutEffect(() => {
    if (!autoStickRef.current) {
      // A new turn arrived while the learner was reading history — surface a container-local jump cue.
      if (convScrollRef.current) setShowJump(true);
      return;
    }
    const raf = requestAnimationFrame(() => {
      const el = convScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [convSig, streamingCoaching?.content, busy]);
  // Highlight, in place, the sentence(s) The Writing Studio currently recognizes as the thesis —
  // always the same text shown in the YOUR THESIS panel (activeCoaching.current_thesis).
  const docRef = useRef(null);
  const highlightRef = useRef(null);
  // Item 3 — on desktop, the Writing Studio dialogue column ends exactly at the BOTTOM of the HYPIW/WTRU
  // (structural) box in the left column. The left column continues below it (What next), so the
  // dialogue height is measured rather than stretched. No fixed height on mobile (columns stack).
  const hypiwRef = useRef(null);
  const convBoxRef = useRef(null);
  // WPI P0 — writing-process evidence (non-user-facing). Paste events + coarse composition timing for
  // the CURRENT (unsubmitted) draft version. Reset after each draft submission. No keystroke logging.
  const pasteEventsRef = useRef([]);
  const editStartRef = useRef(null);
  const captureDraftEdit = useCallback(() => {
    if (editStartRef.current == null) editStartRef.current = Date.now();
  }, []);
  const capturePaste = useCallback((e) => {
    // AES: student-authored prose is entered directly so The Writing Studio can follow how the writing develops.
    // Block the paste, record a corroborating-only blocked-paste event, show a neutral message.
    if (getTestMode()) return; // TEST MODE (manual QA): paste allowed, nothing recorded
    try { e.preventDefault(); e.stopPropagation(); } catch (_) {}
    try {
      const pasted = (e.clipboardData || window.clipboardData)?.getData("text") || "";
      pasteEventsRef.current.push({
        at: new Date().toISOString(),
        blocked: true,
        insertion_index: e.target?.selectionStart ?? null,
        char_count: pasted.length,
        word_count: pasted.trim() ? pasted.trim().split(/\s+/).length : 0,
      });
    } catch (_) { /* never surface errors to the student */ }
    toast("The Writing Studio asks you to enter your own writing directly so that it can follow how your writing develops.");
    return false;
  }, []);
  const blockDrop = useCallback((e) => {
    if (getTestMode()) return; // TEST MODE (manual QA): drop allowed
    try { e.preventDefault(); e.stopPropagation(); } catch (_) {}
    try { pasteEventsRef.current.push({ at: new Date().toISOString(), blocked: true, drop: true }); } catch (_) {}
    toast("The Writing Studio asks you to enter your own writing directly so that it can follow how your writing develops.");
    return false;
  }, []);
  const collectTelemetry = useCallback(() => {
    const t = {
      paste_events: pasteEventsRef.current.slice(),
      composition_ms: editStartRef.current != null ? Date.now() - editStartRef.current : null,
      submitted_at: new Date().toISOString(),
    };
    pasteEventsRef.current = [];
    editStartRef.current = null;
    return t;
  }, []);
  const activeThesis = activeCoaching?.current_thesis || "";
  const focusRegionText = activeCoaching?.focus_region || "";
  const focusPortionText = activeCoaching?.focus_portion || "";
  const thesisRanges = useMemo(() => {
    const r = locateUnit(draft, activeThesis);
    return r ? snapRangesToSentences(draft, [r]) : [];
  }, [draft, activeThesis]);
  // Shade the THESIS (blue, the organizing center) + the CURRENT WORKING UNIT (orange, labelled by
  // its actual structure). The working unit is whatever function The Writing Studio is focused on this turn —
  // Elaboration, Evidence, Conclusion, Opening — so it is ALWAYS shaded as The Writing Studio iterates.
  const focusName = activeCoaching?.focus_of_work || "";
  const functionSpans = useMemo(() => activeCoaching?.function_spans || {}, [activeCoaching]);
  const FOCUS_LABELS = {
    Opening: "OPENING",
    Thesis: "THESIS",
    Elaboration: "ELABORATION",
    "Evidence / Example": "EVIDENCE",
    Conclusion: "CONCLUSION",
  };
  const focusLabel = FOCUS_LABELS[focusName] || (focusName || "").toUpperCase();
  const isThesisFocus = focusName === "Thesis" || !focusName;
  // Working-unit signals: the verbatim span of the focused function + its focus region. Union into
  // ONE contiguous span so the ENTIRE working unit is shaded/labelled — never a fragment.
  const activeUnitSignals = useMemo(() => {
    const arr = [];
    if (isThesisFocus) return arr; // thesis handled separately (blue)
    if (functionSpans[focusName]) arr.push(functionSpans[focusName]);
    if (focusRegionText) arr.push(focusRegionText);
    return arr;
  }, [functionSpans, focusName, focusRegionText, isThesisFocus]);
  const activeUnitRanges = useMemo(() => {
    const spans = activeUnitSignals.map((t) => locateUnit(draft, t)).filter(Boolean);
    if (!spans.length) return [];
    const start = Math.min(...spans.map((r) => r[0]));
    const end = Math.max(...spans.map((r) => r[1]));
    // ONE contiguous span (earliest→latest matched sentence), snapped to full sentences,
    // then clipped so it never overlaps the Thesis span.
    const span = snapRangesToSentences(draft, [[start, end]]);
    return subtractRanges(span, thesisRanges);
  }, [draft, activeUnitSignals, thesisRanges]);
  // Focus portion: the one passage under discussion (L3). When the engine leaves focus_portion
  // empty it means the WHOLE focus region is the focus — fall back to it.
  const portionText = focusPortionText || focusRegionText;
  const hostRanges = isThesisFocus ? thesisRanges : activeUnitRanges;
  const portionRanges = useMemo(() => {
    if (!portionText) return [];
    const loose = locateUnit(draft, portionText);
    const found = loose ? [loose] : findThesisRanges(draft, portionText);
    const raw = found.map(([s, e]) => {
      let a = s;
      let b = e;
      while (a < b && /\s/.test(draft[a])) a++;
      while (b > a && /\s/.test(draft[b - 1])) b--;
      return [a, b];
    });
    // keep the underline inside the active communicative unit only
    return raw
      .map(([s, e]) => {
        const host = (hostRanges || []).find((r) => s < r[1] && e > r[0]);
        return host ? [Math.max(s, host[0]), Math.min(e, host[1])] : null;
      })
      .filter((r) => r && r[1] > r[0]);
  }, [draft, portionText, hostRanges]);
  // Underline colour matches its unit: blue inside the thesis, orange inside the working unit.
  const focusClass = isThesisFocus ? "vi-focus-thesis" : "vi-focus-active";
  // Regions (single element each so the label renders once): Thesis (blue) + working unit (orange).
  const interpretationRegions = useMemo(() => {
    const regs = [];
    thesisRanges.forEach(([s, e]) =>
      regs.push({
        start: s,
        end: e,
        className: "vi-region vi-region-thesis",
        label: "THESIS",
        testid: "vi-thesis-region",
      })
    );
    if (!isThesisFocus) {
      activeUnitRanges.forEach(([s, e]) =>
        regs.push({
          start: s,
          end: e,
          className: "vi-region vi-region-active",
          label: focusLabel || "WORKING",
          testid: "vi-active-region",
        })
      );
    }
    return regs;
  }, [thesisRanges, activeUnitRanges, isThesisFocus, focusLabel]);
  // The Writing Studio 4.10 — SENTENCE CRAFT: once the paragraph is structurally ready, foreground the ONE
  // active sentence WITHIN the intact paragraph. Located by TEXT (robust to index shifts on revision),
  // matching the existing thesis-highlight visual language: left accent bar + subtle background, and
  // the rest of the paragraph left at full opacity (context is never visually suppressed).
  const sc = activeCoaching?.sentence_craft || null;
  const scActive = !!(sc && sc.active && sc.decision !== "complete" && (sc.active_sentence_text || "").trim());
  const scComplete = !!(sc && sc.decision === "complete");
  const scProgress = sc?.progress || null;
  // TWO-ROUND bounded experience — the deterministic episode snapshot from the latest AI turn.
  const episode = activeCoaching?.episode || null;
  const scOfferPending = !!episode?.sc_offer_pending;
  const episodeFinished = !!episode?.episode_finished;
  const proofreadingActive = episode?.round3_state === "ACTIVE" || episode?.round3_state === "PF_CLOSURE";
  // PROOFREADING CLOSURE (voluntary continuation): the required proofreading pass is satisfied but real
  // findings remain. Present the choice (keep proofreading / continue to final review); never the fix.
  const pfClosure = (episode?.round3_state === "PF_CLOSURE") ? (episode?.pf_closure || null) : null;
  // COMPOSITION → SENTENCE CRAFT READINESS. When Round-1 Composition has determined the paragraph is
  // TWO_ROUND is on for the URL-flagged experience, the active OT→Old-Composition bridge, OR any session
  // already in two-round mode (so a page RELOAD of a bridged session still renders the two-pane workspace).
  const TWO_ROUND = TWO_ROUND_URL || bridgedComposition || session?.episode_mode === "two_round";
  // communicatively sufficient (round1.state === "CLOSING", and the learner did not end it themselves),
  // the interface must behave as though Composition is complete: a dominant "Continue to Sentence Craft"
  // transition — NOT a revision screen. (The backend intentionally does not set sc_offer_pending here;
  // the CLOSING state is the readiness signal.)
  const compositionReady = TWO_ROUND && !scActive && !scComplete && !proofreadingActive && !episodeFinished
    && episode?.active_round === 1
    && episode?.round1?.state === "CLOSING"
    && !episode?.round1?.learner_ended;
  // UNIFIED SENTENCE-CRAFT HANDOFF. Composition Ready (round1.state=CLOSING) and SC Offer Pending
  // (round1.state=COMPLETE, sc_offer_pending) are the SAME instructional reality: Composition work is
  // done and the learner's primary next action is Sentence Craft (revision is optional). Both states use
  // the one dominant transition treatment; the stale "Revise / Send revision" affordance is suppressed.
  const showScHandoff = (compositionReady || scOfferPending) && !keepRefining;
  // TESTING BRIDGE — paragraph cursor state for the OT→Old-Composition path.
  const bridgeMulti = !!(bridgeCursor && bridgeCursor.total > 1);
  const bridgeHasMore = !!(bridgeMulti && bridgeCursor.index < bridgeCursor.total - 1);
  const proofreadingOffer = (episode?.round3_state === "OFFERED") ? (episode?.proofreading_offer || null) : null;
  // RELATIONAL DISPLAY (Message 431): the visible instructional object must match the structural
  // object being taught. When the phenomenon lives in the relationship BETWEEN sentences, the payload
  // carries display_units for BOTH sides — highlight each (active = "THIS SENTENCE", related = "RELATED")
  // so the learner never reasons about a relationship while seeing only one side of it.
  const scRegions = useMemo(() => {
    if (!scActive) return [];
    const units =
      sc.display_units && sc.display_units.length
        ? sc.display_units
        : [{ text: sc.active_sentence_text, role: "active" }];
    const regs = [];
    units.forEach((u) => {
      if (!u || !(u.text || "").trim()) return;
      const r = locateUnit(draft, u.text);
      if (!r) return;
      snapRangesToSentences(draft, [r]).forEach(([s, e]) =>
        regs.push({
          start: s,
          end: e,
          className:
            u.role === "related" ? "vi-region vi-region-sc-related" : "vi-region vi-region-sc",
          label: u.role === "related" ? "RELATED" : "THIS SENTENCE",
          testid: u.role === "related" ? "vi-sc-related-region" : "vi-sc-active-region",
        })
      );
    });
    return regs;
  }, [draft, scActive, sc]);
  // PROOFREADING FLAGS — highlight EVERY unresolved mechanical flag directly in the paragraph so the
  // learner can see exactly what to fix (§ user request). Locate each finding's excerpt by text (robust
  // to index shifts); drop non-locatable descriptive formatting flags; skip overlaps (the segment
  // renderer requires non-overlapping regions).
  const pfFindings = episode?.proofreading_view?.findings || [];
  const pfRegions = useMemo(() => {
    if (!proofreadingActive) return [];
    const found = [];
    (pfFindings || []).forEach((f) => {
      const txt = (f.text || "").trim();
      if (!txt || txt.length < 2 || f.category === "formatting") return;
      const r = locateUnit(draft, txt);
      if (r && r[1] > r[0]) found.push({ start: r[0], end: r[1], label: (f.label || "Fix") });
    });
    found.sort((a, b) => a.start - b.start || b.end - a.end);
    const regs = [];
    found.forEach((r) => {
      const prev = regs[regs.length - 1];
      if (prev && r.start < prev.end) {
        // Overlapping flags: UNION the spans (and merge labels) so no finding is silently un-highlighted
        // — e.g. a missing-period tail that overlaps a capitalization span stays visibly marked.
        prev.end = Math.max(prev.end, r.end);
        const lbl = r.label.toUpperCase();
        if (!prev.label.includes(lbl)) prev.label += " / " + lbl;
        return;
      }
      regs.push({ start: r.start, end: r.end, className: "vi-region vi-region-pf",
                  label: r.label.toUpperCase(), testid: "vi-pf-region" });
    });
    return regs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proofreadingActive, draft, episode?.proofreading_view?.current_finding_id, pfFindings.length]);
  // While Sentence Craft is active, render ONLY the active-sentence region (no thesis/working-unit
  // shading competing for attention) so the active sentence is foregrounded but the paragraph stays whole.
  const renderRegions = proofreadingActive ? pfRegions : scActive ? scRegions : interpretationRegions;
  const renderPortionRanges = scActive || proofreadingActive ? [] : portionRanges;
  const renderFocusClass = scActive ? "vi-focus-sc" : focusClass;
  // NAVIGATION / ORIENTATION (UX pass): current workflow stage + the explicit next action + mode.
  const currentStage =
    (showEpisodeReview || episodeFinished) ? "final"
    : (proofreadingActive || proofreadingOffer) ? "proofreading"
    : (episode?.active_round === 2) ? "sentence_craft"
    : "composition";
  const workflowMode = proofreadingActive ? "proofread" : scActive ? "revise" : "compose";
  // ORIENTATION LAYER (Beta 1) — student-facing journey/NOW derived ONLY from existing later-stage state.
  const journeyPathway = mode === "ot" ? "full" : "composition";
  // Structural diagram source (relocated to the LEFT column, above the writing box).
  const r1 = episode?.round1 || null;
  const vmMap = r1?.visual_map || null;
  // "Where your paragraph stands" — deterministic readiness + remaining opportunities derived ONLY from
  // the existing visual-map / reader-model analysis (no new LLM call). Budget met = sufficient to advance,
  // NOT perfect. Each remaining item corresponds to a real unmet communicative function.
  const paragraphStanding = useMemo(() => {
    const vm = r1?.visual_map || {};
    const rm = vm.reader_model || {};
    const els = Array.isArray(vm.elements) ? vm.elements : [];
    const unit = bridgeMulti ? "This part" : "The paragraph";
    const STRENGTHEN = {
      "OPENING": `${unit} could open by making it clearer what it's about.`,
      "THESIS": "Your main point could come through more clearly.",
      "ELABORATION": "One of your ideas could be explained more fully.",
      "EVIDENCE / EXAMPLE": "Your example could be explained more clearly to show what it proves.",
      "CONCLUSION": `${unit} could reach a clearer sense of closing.`,
    };
    const PRIORITY = { "THESIS": 0, "ELABORATION": 1, "EVIDENCE / EXAMPLE": 2, "OPENING": 3, "CONCLUSION": 4 };
    const working = (rm.understands_sentence || "").trim()
      || `${bridgeMulti ? "This part is" : "Your paragraph is"} communicating its main ideas clearly enough to move on.`;
    const remaining = els
      .filter((e) => e.status === "current" || e.status === "to_come")
      .sort((a, b) => (a.status === "current" ? 0 : 1) - (b.status === "current" ? 0 : 1) || ((PRIORITY[a.key] ?? 9) - (PRIORITY[b.key] ?? 9)))
      .map((e) => STRENGTHEN[e.key] || (e.label ? `${e.label} could be developed further.` : ""))
      .filter(Boolean);
    let issues = Array.from(new Set(remaining)).slice(0, 3);
    if (!issues.length && (rm.still_needs || "").trim()) issues = [(rm.still_needs || "").trim()];
    return { working, issues };
  }, [r1, bridgeMulti]);
  // DIRECT (single-flow) Composition ships only the deterministic map (no round contract): show the
  // Paragraph Function Map whenever a map exists, hidden during Sentence Craft / Proofreading.
  const directComposition = !!episode && episode.mode === "direct";
  const hasStructuralMap = !proofreadingActive && !!vmMap?.map_state && (
    directComposition ? !scActive : (episode?.active_round === 1 && ["WORKING", "CLOSING"].includes(r1?.state)));
  const workedThrough = r1?.completed || [];
  // ORIENTATION SIGNALS — existing state only (no new state, no new logic); see lib/orientation.js.
  const orientationSignals = useMemo(() => {
    const cur = bridgeCursor?.current || null;
    return {
      sessionId: session?.id || "",
      pathway: journeyPathway,
      screen: currentStage === "final" ? "final" : currentStage === "proofreading" ? "proofreading"
        : currentStage === "sentence_craft" ? "sentence_craft" : "composition",
      comp: {
        currentElement: vmMap?.current_element || "",
        precedenceActive: !!vmMap?.structural_precedence?.active,
        closing: r1?.state === "CLOSING",
        multi: !!bridgeMulti,
        section: cur ? { sectionId: cur.section_id || "", title: cur.section_title || "", paragraphIndex: cur.paragraph_index || 0 } : null,
        openDemands: (r1?.preserved_demands || []).filter((e) => e && e.status === "open").length,
        hasMap: !!hasStructuralMap,
        direct: !!directComposition,
      },
    };
  }, [session?.id, journeyPathway, currentStage, vmMap, r1, bridgeMulti, bridgeCursor, hasStructuralMap, directComposition]);
  const nextActionText = proofreadingActive
    ? `Fix the highlighted issue in ${bridgeMulti ? "your writing" : "your paragraph"} above, then press \u201cSend corrected ${bridgeMulti ? "writing" : "paragraph"}.\u201d If it's intentional, mark it, or skip to your final review.`
    : scActive
    ? `Revise the highlighted sentence in ${bridgeMulti ? "your writing" : "your paragraph"} above, then press \u201cSend revision.\u201d`
    : `Revise ${bridgeMulti ? "your writing" : "your paragraph"} above to work on this, then press \u201cSend revision.\u201d You can also reply below to talk it through with The Writing Studio.`;

  // ── BACKWARD NAVIGATION + STALENESS (frontend-only; no LLM; non-destructive) ─────────────────
  const STAGE_ORDER = ["composition", "sentence_craft", "proofreading", "final"];
  const STAGE_LABEL = { composition: "Composition", sentence_craft: "Sentence Craft", proofreading: "Proofreading", final: "Final Review" };
  const stageSnapKey = session?.id ? `compass_stagesnap_${session.id}` : null;
  const [stageSnap, setStageSnap] = useState({});
  useEffect(() => {
    if (!stageSnapKey) return;
    try { setStageSnap(JSON.parse(localStorage.getItem(stageSnapKey) || "{}")); } catch { setStageSnap({}); }
  }, [stageSnapKey]);
  const recordStageSnap = useCallback((stageKey) => {
    if (!stageSnapKey || !stageKey) return;
    setStageSnap((prev) => {
      const next = { ...prev, [stageKey]: draft };
      try { localStorage.setItem(stageSnapKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [stageSnapKey, draft]);
  const staleStageKeys = useMemo(() => {
    const norm = (draft || "").trim();
    return Object.keys(stageSnap).filter((k) => k !== currentStage && (stageSnap[k] || "").trim() && (stageSnap[k] || "").trim() !== norm);
  }, [stageSnap, draft, currentStage]);
  const reachedStageKeys = useMemo(() => {
    const ci = STAGE_ORDER.indexOf(currentStage);
    const base = STAGE_ORDER.filter((_, i) => i <= ci);
    return Array.from(new Set([...base, ...Object.keys(stageSnap)]));
  }, [currentStage, stageSnap]);
  const viewingPast = !!viewStage && viewStage !== currentStage;
  const navigateStage = useCallback((key) => {
    if (key === currentStage) { setViewStage(null); return; }
    const ci = STAGE_ORDER.indexOf(currentStage);
    const ti = STAGE_ORDER.indexOf(key);
    if (ti >= 0 && ti <= Math.max(ci, ...reachedStageKeys.map((k) => STAGE_ORDER.indexOf(k)))) setViewStage(key);
  }, [currentStage, reachedStageKeys]);
  // BACK NAVIGATION (composition-family screens): step to the previous stage the student has reached
  // (Final→Proofreading→Sentence Craft→Composition). At the first composition stage, open My Work
  // (non-destructive). Pure navigation — no backend calls, no state reversal.
  const goBackStage = useCallback(() => {
    const ai = STAGE_ORDER.indexOf(viewStage || currentStage);
    if (ai > 0) { navigateStage(STAGE_ORDER[ai - 1]); return; }
    setShowMyWork(true);
  }, [viewStage, currentStage, navigateStage]);

  // ── CANONICAL ACTIVE SENTENCE (Sentence Craft) — one source, two views (7A) ──────────────────
  // The full-composition highlight AND the focused CURRENT PASSAGE editor both derive from the SAME
  // sc.active_sentence_text located against the live draft. Editing the passage splices back into the
  // one draft (no divergent copy). If the text can't be located, we still SURFACE the canonical text.
  // The CURRENT PASSAGE editor is a STABLE editing buffer (like composition Focus): its range is
  // established ONCE per SC target (when sc.active_sentence_text / index changes) and then edited freely —
  // never re-derived from string-matching on each keystroke. This lets boundary-changing edits (inserting
  // or removing a period, splitting/merging a sentence) proceed without the located range collapsing and
  // reverting the box to the stale sentence. When a revision yields a NEW SC target, the buffer is
  // re-established from the CURRENT canonical draft (so it can never diverge from the accepted text).
  const [scBuf, setScBuf] = useState(null); // {start,end,text}
  const scBufRef = useRef(null);
  useEffect(() => { scBufRef.current = scBuf; }, [scBuf]);
  const scTargetKey = `${sc?.active_sentence_index ?? ""}::${(sc?.active_sentence_text || "").trim()}`;
  const scBufInitRef = useRef(null);
  useEffect(() => {
    if (!scActive) { scBufInitRef.current = null; setScBuf(null); return; }
    if (scBufInitRef.current === scTargetKey) return; // same target — keep the in-progress buffer
    scBufInitRef.current = scTargetKey;
    const t = (sc?.active_sentence_text || "").trim();
    const r = t ? locateUnit(draft, t) : null;
    if (!r) { setScBuf(null); return; }
    const snapped = snapRangesToSentences(draft, [r]);
    let [a, b] = (snapped && snapped[0]) || r;
    while (a < b && /\s/.test(draft[a])) a++;
    while (b > a && /\s/.test(draft[b - 1])) b--;
    setScBuf({ start: a, end: b, text: draft.slice(a, b) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scActive, scTargetKey]);
  const activeScRange = scBuf ? [scBuf.start, scBuf.end] : null;
  const activeSentenceLive = scBuf ? scBuf.text : (sc?.active_sentence_text || "");
  const spliceActiveSentence = useCallback((newText) => {
    const b = scBufRef.current;
    if (!b) return;
    const { start, end } = b;
    captureDraftEdit();
    setDraft((d) => d.slice(0, start) + newText + d.slice(end));
    const nb = { start, end: start + newText.length, text: newText };
    scBufRef.current = nb;
    setScBuf(nb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Generalized focus target: Sentence Craft isolates the active sentence; Composition isolates the
  // SENTENCE(S) the current instruction is about (its focus region), NOT the whole paragraph. Both edit
  // the SAME draft (splice back), so changes appear in the text box immediately.
  const compositionFocus = !scActive && episode?.active_round === 1 && !proofreadingActive;
  const compFocusText = (focusRegionText || focusPortionText || "").trim();
  // Composition focus is a STABLE editing buffer: {start,end,text}. It is established once (when the box
  // opens or the targeted passage changes) and thereafter the learner edits it FREELY — we never re-derive
  // its bounds from the live draft on keystrokes, so typing anywhere (including the very end) is unlimited,
  // and the draft/text-box is NOT re-imported into the box while the learner is editing.
  const [compFocus, setCompFocus] = useState(null);
  const compFocusRef = useRef(null);
  useEffect(() => { compFocusRef.current = compFocus; }, [compFocus]);
  const compFocusInitRef = useRef(null);
  useEffect(() => {
    if (!compositionFocus || !focusPassageOpen) { compFocusInitRef.current = null; return; }
    const key = compFocusText || "all";
    if (compFocusInitRef.current === key) return; // already established for this passage — don't re-import
    compFocusInitRef.current = key;
    const t = compFocusText;
    if (!t) { setCompFocus(null); return; }
    const r = locateUnit(draft, t);
    if (!r) { setCompFocus(null); return; }
    const snapped = snapRangesToSentences(draft, [r]);
    let [a, b] = (snapped && snapped[0]) || r;
    while (a < b && /\s/.test(draft[a])) a++;
    while (b > a && /\s/.test(draft[b - 1])) b--;
    setCompFocus({ start: a, end: b, text: draft.slice(a, b) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compositionFocus, focusPassageOpen, compFocusText]);
  const spliceCompFocus = useCallback((newText) => {
    const cf = compFocusRef.current;
    if (!cf) return;
    const { start, end } = cf;
    captureDraftEdit();
    setDraft((d) => d.slice(0, start) + newText + d.slice(end));
    const nb = { start, end: start + newText.length, text: newText };
    compFocusRef.current = nb;
    setCompFocus(nb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const hasCompFocus = !!(compositionFocus && compFocus);
  const focusAvailable = !viewingPast && ((scActive && (activeSentenceLive || "").trim()) || (compositionFocus && (draft || "").trim()));
  const focusValue = scActive ? activeSentenceLive : (hasCompFocus ? compFocus.text : draft);
  const focusEditable = scActive ? !!activeScRange : true;
  const focusOnChange = scActive
    ? spliceActiveSentence
    : (hasCompFocus ? spliceCompFocus : (v) => { captureDraftEdit(); setDraft(v); });
  const focusScoped = scActive || hasCompFocus;
  const focusTitle = scActive
    ? `Focus \u00b7 this sentence${sc?.sentence_count ? ` (${(sc.active_sentence_index ?? 0) + 1}/${sc.sentence_count})` : ""}`
    : (hasCompFocus ? "Focus \u00b7 this passage" : (bridgeMulti ? "Focus \u00b7 this part" : "Focus \u00b7 your paragraph"));
  // Compressed instruction for the focus box — the key ask from the latest coaching (prefer the question),
  // not the whole coaching turn.
  const focusInstruction = useMemo(() => {
    const t = (conversation.filter((m) => m.who === "compass").slice(-1)[0] || {}).text || "";
    if (!t) return sc?.focus_phrase || "";
    const sents = t.split(/(?<=[.?!])\s+/).map((s) => s.trim()).filter(Boolean);
    const q = [...sents].reverse().find((s) => s.endsWith("?"));
    return (q || sents[0] || t).slice(0, 240);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.length, sc]);
  // Focus box STARTS OPEN on each new target (a new SC sentence, or entering Composition coaching); if
  // the learner closes it, it stays closed until the target changes (or they reopen with Focus).
  useEffect(() => {
    if (viewingPast) { setFocusPassageOpen(false); focusSeenRef.current = null; return; }
    // STAGE CLOSURE → NO ACTIVE TARGET → NO ACTIVE FOCUS. When a stage reaches its completion
    // condition, close the Focus panel and clear the active-target visual state so the completion
    // transition reads cleanly (student can reopen a real target only if they choose to keep refining).
    const compClosed = episode?.round1?.decision === "complete" || !!episode?.sc_offer_pending;
    const scClosed = sc?.decision === "complete";
    const stageClosed = (compositionFocus && compClosed) || (scActive && scClosed);
    if (stageClosed) { setFocusPassageOpen(false); focusSeenRef.current = null; return; }
    if (scActive && (activeSentenceLive || "").trim()) {
      const key = "sc-" + String(sc?.active_sentence_index ?? "x");
      if (focusSeenRef.current !== key) { setFocusPassageOpen(true); focusSeenRef.current = key; }
    } else if (compositionFocus && (draft || "").trim() && activeCoaching) {
      const key = "compose-" + (compFocusText || "all");
      if (focusSeenRef.current !== key) { setFocusPassageOpen(true); focusSeenRef.current = key; }
    } else if (!scActive && !compositionFocus) {
      setFocusPassageOpen(false); focusSeenRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scActive, sc?.active_sentence_index, activeSentenceLive, compositionFocus, compFocusText, activeCoaching, viewingPast]);
  // Drag the focus box by its header (no backdrop, so the writing box stays fully usable).
  const startFocusDrag = useCallback((ev) => {
    ev.preventDefault();
    const startX = ev.clientX, startY = ev.clientY;
    const base = focusPos || { x: Math.max(24, (window.innerWidth / 2) - 260), y: 130 };
    const onMove = (e) => setFocusPos({ x: base.x + (e.clientX - startX), y: base.y + (e.clientY - startY) });
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  }, [focusPos]);

  // PERCEIVED-LATENCY (evaluation status): what The Writing Studio is actually checking while the engine works.
  // Derived ENTIRELY from real prior-turn instructional state — the focus we were coaching and the
  // reader gap we were pursuing — never fabricated. Reframes the deterministic reader-model gap
  // (episode.round1.visual_map.reader_model.still_needs) into a "checking whether…" line.
  const evalFocus = useMemo(() => {
    // Proofreading: mechanics correction just submitted.
    if (proofreadingActive) {
      const cat = (pfFindings[0]?.label || "").trim();
      return {
        label: "Proofreading",
        check: cat
          ? `Checking whether the ${cat.toLowerCase()} now reads cleanly\u2026`
          : "Checking whether your corrections now read cleanly\u2026",
      };
    }
    // Sentence Craft: a sentence-level revision just submitted.
    if (scActive) {
      const fl = (sc?.focus_label || "").trim();
      return {
        label: fl || "Sentence Craft",
        check: "Checking whether your revised sentence now communicates more clearly\u2026",
      };
    }
    // Composition (Round 1): reframe the real reader-model gap into a check.
    const rm = episode?.round1?.visual_map?.reader_model || null;
    const label = focusName || "Your paragraph";
    const reframed = reframeStillNeeds((rm?.still_needs || "").trim());
    if (reframed) return { label, check: reframed };
    // Focus-specific honest fallback when no reader-model gap is present.
    const byFocus = {
      Elaboration: "Checking whether the reasoning behind your point now comes through\u2026",
      Thesis: "Checking whether your main point now comes through clearly\u2026",
      "Evidence / Example": "Checking whether your example now grounds the point\u2026",
      Conclusion: "Checking whether your paragraph now reaches a sense of completion\u2026",
      Opening: "Checking whether it\u2019s now clear what this paragraph is about\u2026",
    };
    return { label, check: byFocus[focusName] || "Checking whether your revision strengthens the paragraph\u2026" };
  }, [proofreadingActive, pfFindings, scActive, sc, episode, focusName]);

  // Live context for in-context (rolling) feedback — captures exactly what the learner
  // is looking at: stage, coaching target, recent conversation, and the current draft.
  const getFeedbackContext = useCallback(() => {
    const stageLabel = {
      composition: "Composition", sentence_craft: "Sentence Craft",
      proofreading: "Proofreading", final: "Final Review",
    }[currentStage] || currentStage;
    const coachingTarget =
      currentStage === "sentence_craft" ? (sc?.operation || sc?.focus_label || focusName || "") :
      currentStage === "proofreading" ? ((pfFindings[0]?.label) || "Proofreading") :
      (focusName || "");
    const sTurns = studentTurns || [];
    const lastLearner = sTurns.length ? (sTurns[sTurns.length - 1].content || "") : "";
    const recentTurns = (allTurns || []).slice(-4).map((t) => ({
      role: t.role, kind: t.kind || "", content: (t.content || "").slice(0, 1500),
    }));
    return {
      user_id: user?.id || null,
      stage: stageLabel,
      coaching_target: coachingTarget,
      assignment: assignment || "",
      current_paragraph: draft || "",
      draft: draft || "",
      last_compass_message: (activeCoaching?.content || "").slice(0, 4000),
      last_learner_message: (lastLearner || "").slice(0, 4000),
      recent_turns: recentTurns,
    };
  }, [currentStage, sc, focusName, pfFindings, studentTurns, allTurns, user, assignment, draft, activeCoaching]);  // Auto-grow the writing canvas so the WHOLE draft is always visible (no inner scroll / cut-off).
  // The overlay is absolute inset-0, so growing the textarea grows the container and keeps them aligned.
  const fitDoc = useCallback(() => {
    const el = docRef.current;
    if (!el) return;
    el.style.overflowY = "hidden";
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useEffect(() => {
    fitDoc();
    window.addEventListener("resize", fitDoc);
    return () => window.removeEventListener("resize", fitDoc);
  }, [fitDoc, draft, writingStarted, otPhase, session]);
  const started = !!session;
  const reviseCount = studentTurns.filter((t) => t.kind === "revise").length;
  // Chapter 6 — the "first encounter" is the learner's first draft and The Writing Studio's
  // first response to it. Pedagogical Noticing runs only here; later revisions
  // keep the established relationship (existing coaching behavior).
  const isFirstMoment = started && reviseCount === 0;
  // Completion is driven ONLY by the single-objective experience_control phase,
  // never by an AI turn count.
  const phase = session?.experience_control?.phase || "active";
  const inReflection = phase === "reflection";
  // Mode separation: 'teacher' = teacher-simulation wrapper (Experience The Writing Studio
  // welcome + create-assignment + teacher reflection). 'writing' / 'ot' = GENUINE
  // student entries — the learner completes an assignment they were given, with
  // no teacher framing.
  const isTeacher = mode === "teacher";
  const isStudent = !isTeacher;
  const hasStudentTurn = studentTurns.length > 0;
  const showWelcome = isTeacher && !entered && !session;
  const showAssignment = isTeacher && entered && !session && !writingStarted && !otPhase;
  const showStudentEntry = isStudent && !session && !writingStarted && !otPhase && !hasStudentTurn;
  const showOT = otPhase && !hasStudentTurn;
  const showWriting = writingStarted && !hasStudentTurn && !otPhase;

  // Poll while the engine is reasoning in the background.
  useEffect(() => {
    if (!session?.id || !isProcessing) return;
    const id = session.id;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const s = await getSession(id);
        if (!cancelled) setSession(s);
      } catch (e) {
        /* transient — keep polling */
      }
    }, 900);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [session?.id, isProcessing]);

  // A new coaching target auto-surfaces its marker. On the FIRST encounter the
  // developmental response auto-expands so it flows continuously from the
  // noticing beats (no marker click, no abrupt screen change).
  useEffect(() => {
    if (activeCoaching) {
      setCardOpen(true);
      // Auto-surface EVERY new coaching turn (not just the first) so the learner never has to hunt
      // for feedback or click a marker to discover that The Writing Studio has responded.
      setOpenCoachingId(activeCoaching.id);
      // A new turn arrived — re-offer the Sentence-Craft transition (reset any "keep refining" choice).
      setKeepRefining(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCoaching?.id]);


  // The session roadmap re-shows only for a genuinely NEW experience (reset in restart()), NOT when
  // the session object is created mid-flow — so it appears BEFORE writing, never after the paragraph.

  // Reveal the interim observations as a paced, grounded sequence. The FIRST
  // observation is held until ~6.5s (5–8s window) after the learner submitted (so
  // it reads as a genuine reading of their writing, never an instant message); a
  // SECOND grounded observation, if present, follows ~3s later. They never delay
  // the full coaching response, which replaces them when reasoning completes.
  useEffect(() => {
    if (!noticing || !(noticing.observations || []).length) {
      setRevealStage(0);
      return;
    }
    const elapsed = Date.now() - (submitAtRef.current || 0);
    const firstDelay = Math.max(0, 6500 - elapsed); // ~5-8s before the first observation
    const t1 = setTimeout(() => setRevealStage((s) => (s < 1 ? 1 : s)), firstDelay);
    const t2 = setTimeout(() => setRevealStage((s) => (s < 2 ? 2 : s)), firstDelay + 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [noticing]);

  // Student entry → create/resume the session with the student's OWN assignment
  // (the task they were given), then enter the selected student workflow. Same
  // session model; no teacher-experience state attached.
  const enterStudent = useCallback(async (assignmentText) => {
    if (starting || !assignmentText.trim()) return;
    setStarting(true);
    try {
      const s = await startPreview({ assignment: assignmentText.trim(), canonical: WANT_CANONICAL, two_round: mode === "writing" ? true : TWO_ROUND });
      setSession(s);
      setAssignment(assignmentText.trim());
      if (mode === "ot") {
        const ot = await otStart(s.id);
        setOtData(ot);
        setOtPhase(true);
      } else {
        setWritingStarted(true);
      }
      try {
        localStorage.setItem("compass_student_session", JSON.stringify({ id: s.id, mode }));
      } catch (e) { /* ignore */ }
    } catch (e) {
      /* stay on entry */
    } finally {
      setStarting(false);
    }
  }, [starting, mode]);

  // My Plan reached sufficiency → hand the student into the EXISTING Writing
  // workflow on the same session. Capture the final OT state so the Writing
  // context panel can display it (session.ot also covers refresh/resume).
  const finishOrganizing = useCallback(async (finalOt) => {
    if (finalOt) setOtData(finalOt);
    setOtPhase(false);
    // FO-UNIFICATION (P4): go straight from the OT living workspace into the existing composition bridge —
    // the FO room is bypassed (FO_INLINE). The student never leaves one representation to enter another.
    if (FO_INLINE && mode === "ot" && session?.id) {
      try {
        const res = await beginCompositionBridge(session.id);
        setSession(res.session);
        setBridgeCursor(res.bridge);
        setBridgedComposition(true);
        setDraft(res.bridge.active_paragraph || "");
        setWritingStarted(true);
      } catch (e) {
        setEssayMode(true);
      }
      return;
    }
    // LEGACY (FO_INLINE=false): OT hands off into the FO TEACHING STAGE before Composition.
    if (mode === "ot" && session?.id) { setFoPhase(true); return; }
    if (mode === "ot") { setEssayMode(true); return; }
    setWritingStarted(true);
  }, [mode, session]);

  // FO complete → enter the EXISTING composition bridge (OT/FO → old-composition functional teaching, one
  // paragraph at a time → Sentence Craft → Proofreading → Episode Review). Falls back to the essay-level
  // controller if the bridge can't start — never strand the student.
  const finishFO = useCallback(async (transitionMessage) => {
    setFoPhase(false);
    if (mode === "ot" && session?.id) {
      try {
        const res = await beginCompositionBridge(session.id);
        setSession(res.session);
        setBridgeCursor(res.bridge);
        setBridgedComposition(true);
        setDraft(res.bridge.active_paragraph || "");
        setWritingStarted(true);
        return;
      } catch (e) {
        setEssayMode(true);
        return;
      }
    }
    setEssayMode(true);
  }, [mode, session]);

  // CASCADE RETURN-AND-RESUME (Checkpoint 2). Step BACK from a later process to an earlier one to resolve a
  // fundamental issue, then RESUME exactly where you left off. Routing is driven by the backend contract; a
  // brief developmental confirmation is shown at each transition (never a silent auto-jump).
  const initiateReturn = useCallback(async ({ from_process, target, from_object }) => {
    if (!session?.id) return;
    const to_process = (FO_INLINE || target?.stage === "OT") ? "ot" : "fo";
    try {
      const res = await cascadeReturn(session.id, {
        from_process, to_process,
        reason: target?.reason || "", diagnosis: target?.diagnosis || "",
        from_object: { ...(from_object || {}), part_key: from_object?.part_key ?? target?.part_key ?? null },
      });
      setReturnConfirmation(res?.confirmation || "");
      setReturnActive(true);
      if (res.route_to === "ot") {
        setFoPhase(false); setWritingStarted(false); setEssayMode(false);
        setRecursionPurpose({ reason: target?.reason || "", developmental_problem: target?.diagnosis || "",
          relevant_labels: from_object?.label ? [from_object.label] : [] });
        setOtPhase(true);
      } else {
        setWritingStarted(false); setEssayMode(false); setOtPhase(false);
        setFoPhase(true);
      }
    } catch (e) {
      toast.error("Couldn't step back just now — your work is safe. Please try again.");
    }
  }, [session]);

  const doResume = useCallback(async () => {
    if (!session?.id) return;
    try {
      const res = await cascadeResume(session.id);
      setReturnConfirmation(res?.confirmation || "");
      const route = res.route_to;
      if (route === "composition") {
        setFoPhase(false); setOtPhase(false); setRecursionPurpose(null);
        try {
          const b = await getCompositionBridge(session.id);
          if (b && b.active) { setBridgeCursor(b); setDraft(b.active_paragraph || ""); }
          setBridgedComposition(true);
        } catch (_) { /* ignore */ }
        setWritingStarted(true);
        if (res?.confirmation) toast.success(res.confirmation, { duration: 8000 });
      } else if (route === "fo") {
        setOtPhase(false); setRecursionPurpose(null); setFoPhase(true);
      } else {
        setWritingStarted(true);
      }
      setReturnActive(false);
    } catch (e) {
      toast.error("Couldn't bring that back just now — your work is safe. Please try again.");
    }
  }, [session]);

  // TESTING BRIDGE — advance the paragraph cursor. Called when Round 1 lands (paragraph communicatively
  // sufficient). Moves to the NEXT paragraph, or — after the last one — reassembles the whole draft and
  // enters Sentence Craft (native). No visible system change to the student.
  const bridgeNext = useCallback(async () => {
    if (!session || busy) return;
    setSending(true);
    try {
      const res = await advanceCompositionBridge(session.id);
      setSession(res.session);
      setBridgeCursor(res.bridge);
      if (res.bridge.more) {
        setDraft(res.bridge.active_paragraph || "");
      } else {
        if (res.bridge.full_draft) setDraft(res.bridge.full_draft);
        await roundChoose("sentence_craft");
      }
    } catch (e) {
      try { const fresh = await getSession(session.id); setSession(fresh); } catch (_) { /* ignore */ }
      toast.error("Couldn't continue just now — please try that again.");
    } finally {
      setSending(false);
    }
  }, [session, busy]);

  // Writing Screen submit — reuse the session when it already exists (came via
  // Organizing Thought); otherwise (direct Writing entry) create it now with the
  // educator's authentic assignment as the authoritative task. Submits the
  // student's EXACT response (validated on trimmed value, transmitted unaltered).
  // COMPOSITION PATH UNIFICATION: the first draft enters THE ONE Composition process through the same
  // entry as the Full Studio (composition-bridge/begin with the draft): same episode mode, same seeding,
  // same section/paragraph walk. A multi-paragraph draft walks paragraph by paragraph — no separate engine.
  const submitResponse = useCallback(async () => {
    if (response.trim().length < 15 || starting) return;
    setPendingAck("Paragraph received");
    setStarting(true);
    submitAtRef.current = Date.now();
    setNoticing(null);
    setRevealStage(0);
    try {
      let s = session;
      if (!s) {
        s = await startPreview({ assignment: assignment.trim(), canonical: WANT_CANONICAL, two_round: true });
        setSession(s);
      }
      const res = await beginCompositionBridge(s.id, response, collectTelemetry());
      setSession(res.session);
      setBridgeCursor(res.bridge);
      setBridgedComposition(true);
      setDraft(res.bridge?.active_paragraph || response);
      setWritingStarted(true);
      // Chapter 6 — establish the relationship first. Fire Pedagogical Noticing
      // in parallel with the frozen engine's background reasoning. If it fails
      // or times out, the generic thinking experience remains (graceful).
      getNoticing(s.id)
        .then((res2) => {
          if (res2 && res2.ok) setNoticing(res2);
        })
        .catch(() => {});
    } catch (e) {
      /* stay on writing screen */
    } finally {
      setStarting(false);
    }
  }, [response, starting, session, assignment, collectTelemetry]);

  // "Try another paragraph" — return to a CLEARED Assignment Screen (never the
  // Welcome Screen during the same visit) and begin a completely fresh session.
  const restart = useCallback(() => {
    setSession(null);
    setAssignment("");
    setResponse("");
    setDraft("");
    setWritingStarted(false);
    setOtPhase(false);
    setFscMode(false);
    setCompositionArrival(false);
    setEssayMode(false);
    setEssayDraft("");
    setEssayReviewed(false);
    setOtData(null);
    setCardOpen(false);
    setOpenCoachingId(null);
    setReplyOpen(false);
    setReply("");
    setReviewingAsTeacher(false);
    setNoticing(null);
    setRevealStage(0);
    setRoadmapSeen(false);   // re-show the session roadmap for the next fresh experience
    try { localStorage.removeItem("compass_student_session"); localStorage.removeItem("compass_ot_session"); } catch (e) { /* ignore */ }
  }, []);

  // Resume an in-progress STUDENT session on refresh/return. The session id is
  // remembered client-side WITH its mode, so we never pull an incompatible
  // session (e.g. a teacher-preview or other-mode session) into this mode.
  // ARCHITECTURE: for a signed-in student the SERVER is authoritative — a classroom session with a snapshotted
  // writing_path is resumed here (Writing Path shell inside Full Studio) whether or not a client pointer survived.
  // Only when no such session exists does the legacy pointer / fresh old-Studio entry apply (standalone use).
  const resumeHandled = useRef(false);
  // ONE STUDENT PRODUCT: a signed-in student is resolved to a Writing-Path session (classroom first, else standalone) or shown the
  // Writing-Path entry. The legacy Studio (ThinkingWorkspace / Essay / Structure) is no longer a student destination.
  const [wpResolving, setWpResolving] = useState(true);
  const [wpEntry, setWpEntry] = useState(false);
  const [wpChoice, setWpChoice] = useState(null);   // a saved piece found at the door: continue it, or start new
  const continueChoice = async () => {
    const r = wpChoice; if (!r) return;
    try { localStorage.setItem("compass_student_session", JSON.stringify({ id: r.session_id, mode, classroom: !!r.assignment_id })); } catch (e) { /* ignore */ }
    await resumeSessionById(r.session_id);
    setWpChoice(null);
  };
  useEffect(() => {
    if (!isStudent || user === undefined || resumeHandled.current) return;
    resumeHandled.current = true;
    if (!user) setWpResolving(false);
    // PREVIEW/TEST: `?fresh=1` (or `?new=1`) forces a COMPLETELY fresh student session — clear the client-side
    // resume pointer and skip rehydration so no old essay_controller / agenda / episode / hierarchy / descent
    // state is pulled back in. Default entry (no flag) still RESUMES the existing session.
    let freshWanted = false;
    try {
      const q = new URLSearchParams(window.location.search);
      freshWanted = q.get("fresh") === "1" || q.get("new") === "1";
    } catch (e) { freshWanted = false; }
    if (freshWanted) {
      try {
        localStorage.removeItem("compass_student_session");
        localStorage.removeItem("compass_ot_session");
      } catch (e) { /* ignore */ }
    }
    let raw;
    try { raw = freshWanted ? null : localStorage.getItem("compass_student_session"); } catch (e) { raw = null; }
    let parsed = null;
    try { parsed = raw ? JSON.parse(raw) : null; } catch (e) { parsed = null; }
    if (parsed && !parsed.id) parsed = null;
    const resumeDoc = (s, needsPathCheck) => {
        if (needsPathCheck && !(s.writing_path && s.assignment_id)) return;   // another mode's own session: leave it alone
        // Honor the ?canon flag: never resume a session whose canonical state does
        // not match the current request. Otherwise a stale consolidated_v2 (legacy)
        // session would be restored under ?canon=1, silently bypassing the canonical
        // path (no Focus of Work, legacy Explanation teaching). Drop it and let the
        // learner start a fresh session with the correct reasoning mode.
        const isCanon = s.reasoning_mode === "canonical_v2";
        if (WANT_CANONICAL !== isCanon) {
          try { localStorage.removeItem("compass_student_session"); } catch (e) { /* ignore */ }
          return;
        }
        const students = (s.turns || []).filter((t) => t.role === "student" && !t.unit_id);   // local-compose transcript turns stay in the workspace
        setSession(s);
        feedbackEvent(s.id, "resume", {});
        setAssignment(s.assignment || "");
        if (s.ot) setOtData(s.ot);
        // RELIABILITY: rehydrate the OT→Old-Composition bridge cursor after a reload so the multi-paragraph
        // 'part X of N' indicator and cursor-aware Round-1 controls survive refresh (no dropped paragraphs).
        if (s.episode_mode === "two_round") {
          setBridgedComposition(true);
          getCompositionBridge(s.id).then((b) => { if (b && b.active) setBridgeCursor(b); }).catch(() => {});
        }
        if (students.length) {
          setDraft(students[students.length - 1].content || "");
        } else if (mode === "ot" && s.ot && !s.ot.handoff_ready) {
          setOtData(s.ot);
          setOtPhase(true);
        } else if (mode === "ot" && s.ot && s.ot.handoff_ready) {
          // OT already handed off. FO_INLINE bypasses the FO room: resume in Composition (the bridge / essay
          // controller inherits the organization). Legacy (FO_INLINE=false) resumes the FO stage if active.
          if (!FO_INLINE && s.fo && s.fo.active && !s.fo.completed) {
            setFoPhase(true);
          } else if (s.episode_mode === "two_round") {
            setBridgedComposition(true);
            setWritingStarted(true);
          } else {
            setEssayMode(true);
          }
        } else {
          setWritingStarted(true);
        }
        // CHECKPOINT 2 — a cascade return open at load takes routing PRECEDENCE (resume in place after a
        // refresh mid step-back). Backend contract is authoritative for where the student should be.
        if (mode === "ot" && s.resume_stack && s.resume_stack.some((c) => !c.resolved)) {
          getResumeStatus(s.id).then((rstat) => {
            if (!rstat || !rstat.active) return;
            const c = rstat.contract || {};
            setReturnActive(true);
            setReturnConfirmation(c.diagnosis || c.reason || "");
            if (c.to_process === "ot") {
              setFoPhase(false); setWritingStarted(false); setEssayMode(false);
              setRecursionPurpose({ reason: c.reason || "", developmental_problem: c.diagnosis || "",
                relevant_labels: (c.from_object && c.from_object.label) ? [c.from_object.label] : [] });
              setOtPhase(true);
            } else {
              setWritingStarted(false); setEssayMode(false); setOtPhase(false); setFoPhase(true);
            }
          }).catch(() => {});
        }
    };
    const resumeLocal = () => {
      if (!parsed) return;
      // Sessions stored before the `classroom` flag existed (mode "writing") are checked against the server: if they are on a
      // Writing Path they resume too — never a fresh old-Studio session instead.
      const needsPathCheck = parsed.mode !== mode && !parsed.classroom;
      getSession(parsed.id)
        .then((s) => {
          if (!s) { try { localStorage.removeItem("compass_student_session"); } catch (e) { /* ignore */ } return; }
          resumeDoc(s, needsPathCheck);
        })
        .catch(() => { try { localStorage.removeItem("compass_student_session"); } catch (e) { /* ignore */ } });
    };
    if (!user) { resumeLocal(); return; }                // anonymous: legacy behaviour only (not a student destination)
    const enter = () => { setWpEntry(true); setWpChoice(null); setWpResolving(false); };
    // TWO DOORS: ?studio=full (mode "ot") resolves the student's Full Studio piece; ?studio=composition (mode "writing") resolves their
    // Strengthen My Writing piece — never the other one, so Strengthen can never slide into the Full Studio sequence.
    // A piece found this way is OFFERED, not silently resumed: the student chooses "continue" or "start new" (the found piece stays in My Work).
    const lookup = () => cMyWritingPathSession(mode === "writing" ? "strengthen_existing" : "full_studio")
      .then((r) => {
        if (!r?.session_id) { enter(); return; }
        setWpChoice(r); setWpResolving(false);
      })
      .catch(enter);
    if (parsed?.classroom && mode !== "writing") {      // chosen explicitly in My Classes — that session first (Full Studio door only)
      getSession(parsed.id).then((s) => { if (s?.writing_path) { resumeDoc(s, false); setWpResolving(false); } else lookup(); }).catch(lookup);
      return;
    }
    lookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // --- Automatic developmental analytics (supplement, never replace, feedback) ---
  useEffect(() => {
    if (session?.id && otData?.current_stage) feedbackEvent(session.id, "stage_enter", { stage: `ot:${otData.current_stage}` });
  }, [otData?.current_stage, session?.id]);
  useEffect(() => {
    if (session?.id && writingStarted) feedbackEvent(session.id, "stage_enter", { stage: "writing" });
  }, [writingStarted, session?.id]);
  useEffect(() => {
    if (session?.id && inReflection) feedbackEvent(session.id, "stage_enter", { stage: "reflection" });
  }, [inReflection, session?.id]);
  // At the natural END of the experience, invite developmental feedback once.
  const endPrompted = useRef(false);
  useEffect(() => {
    if (inReflection && !reviewingAsTeacher && !endPrompted.current) {
      endPrompted.current = true;
      setFeedbackOpen(true);
    }
  }, [inReflection, reviewingAsTeacher]);
  // Record real tab-close / navigation as a best-effort early-exit signal.
  useEffect(() => {
    const onUnload = () => {
      if (session?.id && !inReflection && (hasStudentTurn || otPhase || writingStarted)) {
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/feedback/${session.id}/event`;
          navigator.sendBeacon?.(url, new Blob([JSON.stringify({ event: "early_exit_unload", data: {} })], { type: "application/json" }));
        } catch (e) { /* ignore */ }
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [session?.id, inReflection, hasStudentTurn, otPhase, writingStarted]);

  const dirty = draft.trim() !== (studentTurns[studentTurns.length - 1]?.content || "").trim();

  // Fire the interim-observations side channel for the CURRENT wait (every turn,
  // not just the first). Resets pacing so the grounded observations appear ~3.5s
  // and ~7s into the wait while the deeper reasoning completes.
  const fireNoticing = useCallback((sid) => {
    submitAtRef.current = Date.now();
    setNoticing(null);
    setRevealStage(0);
    getNoticing(sid)
      .then((res) => {
        if (res && res.ok) setNoticing(res);
      })
      .catch(() => {});
  }, []);

  const sendRevision = useCallback(async () => {
    if (!draft.trim() || busy || !session || !dirty) return;
    setPendingAck(proofreadingActive ? "Correction received" : "Revision received");
    setSending(true);
    try {
      const updated = await interact(session.id, { kind: "revise", content: draft.trim(), input_telemetry: collectTelemetry() });
      setSession(updated);
      setOpenCoachingId(null);
      fireNoticing(session.id);
    } catch (e) {
      /* polling / retry */
    } finally {
      setSending(false);
    }
  }, [draft, busy, session, dirty, fireNoticing, proofreadingActive, collectTelemetry]);

  // RELIABILITY RETRY: re-run an interrupted coaching turn from the learner's current text. User-initiated
  // (never automatic → no duplicate LLM calls), and bypasses the `dirty` guard since the text is unchanged.
  const retryLastTurn = useCallback(async () => {
    if (!session || busy) return;
    const text = (draft || "").trim();
    if (!text) { try { setSession(await getSession(session.id)); } catch (_) { /* ignore */ } return; }
    setPendingAck("Retrying");
    setSending(true);
    try {
      const updated = await interact(session.id, { kind: "revise", content: text, input_telemetry: collectTelemetry() });
      setSession(updated);
      fireNoticing(session.id);
    } catch (e) {
      try { setSession(await getSession(session.id)); } catch (_) { /* ignore */ }
    } finally {
      setSending(false);
    }
  }, [session, busy, draft, fireNoticing, collectTelemetry]);

  const sendExplain = useCallback(async () => {
    if (busy || !session) return;
    // Routes through the SAME bounded dialogue mechanism (no new LLM call type) so the clarification
    // enters the one persistent conversation instead of spawning a separate coaching surface.
    try {
      const updated = await askCompass(session.id, "Can you explain that another way — what exactly should I be looking at?", draft);
      setSession(updated);
    } catch (e) { /* transient — polling reconciles */ }
  }, [busy, session, draft]);

  const sendReply = useCallback(async () => {
    if (!reply.trim() || busy || !session) return;
    setPendingAck("Response received");
    setSending(true);
    const content = reply.trim();
    setReply("");
    setReplyOpen(false);
    try {
      const updated = await interact(session.id, { kind: "answer", content });
      setSession(updated);
      fireNoticing(session.id);
    } catch (e) {
      /* ignore */
    } finally {
      setSending(false);
    }
  }, [reply, busy, session, fireNoticing]);

  // Ask The Writing Studio — talk to The Writing Studio about the CURRENT instruction. Does NOT touch the draft, does not
  // count as a revision, and (by construction on the backend) never advances the Sentence Craft
  // controller. Disabled while any turn is processing (concurrency rule).
  const sendDialogue = useCallback(async () => {
    if (!askMsg.trim() || busy || !session) return;
    const message = askMsg.trim();
    const dc = difficultyCheck(message, diffMemRef.current); diffMemRef.current = dc.memory;
    if (dc.trigger) { setDifficulty({ kind: dc.trigger, at: Date.now() }); return; }   // typed text stays in the box
    setAskMsg("");
    try {
      const updated = await askCompass(session.id, message, draft);
      setSession(updated);
    } catch (e) {
      /* 409 while processing or transient — polling will reconcile */
    }
  }, [askMsg, busy, session, draft]);

  // ORIENTATION — hand a stated difficulty / help request to the EXISTING dialogue (same bounded mechanism as above).
  const orientSendToCoach = useCallback(async (text) => {
    if (!text || busy || !session) return;
    try { setSession(await askCompass(session.id, text, draft)); } catch (e) { /* transient — polling reconciles */ }
  }, [busy, session, draft]);
  const difficultyDone = useCallback((result) => { setDifficulty(null); if (result === "sent") setAskMsg(""); }, []);

  // LEARNER CONTROL over the Sentence Craft episode (§4). Records the stop preference; "stop" then
  // re-runs the current draft through the pipeline so the honored completion turn appears immediately.
  // The Writing Studio never argues with or pressures a learner who chooses to stop.
  const scSetControl = useCallback(async (action) => {
    if (!session || busy) return;
    try {
      const res = await scControl(session.id, action);
      if (res?.rerun && draft.trim()) {
        setPendingAck("Revision received");
        setSending(true);
        const updated = await interact(session.id, { kind: "revise", content: draft });
        setSession(updated);
        fireNoticing(session.id);
      }
    } catch (e) {
      /* transient — polling reconciles */
    } finally {
      setSending(false);
    }
  }, [session, busy, draft, fireNoticing]);

  // TWO-ROUND control: Round 1 landed -> learner chooses Sentence Craft, finishes, or stops early.
  const roundChoose = useCallback(async (choice, content) => {
    if (!session || busy) return;
    setSending(true);
    try {
      const updated = await roundChoice(session.id, choice, content);
      setSession(updated);
      if (choice === "sentence_craft") fireNoticing(session.id);
      if (choice === "finish" || choice === "skip_proofreading" || choice === "self_review_done") setShowEpisodeReview(true);
    } catch (e) {
      // A stale streaming/processing turn can 409 the first attempt; the server finalizes it on the
      // retry, so try once more before surfacing anything — the button must never silently do nothing.
      try {
        const updated = await roundChoice(session.id, choice, content);
        setSession(updated);
        if (choice === "sentence_craft") fireNoticing(session.id);
        if (choice === "finish" || choice === "skip_proofreading" || choice === "self_review_done") setShowEpisodeReview(true);
      } catch (e2) {
        try {
          const fresh = await getSession(session.id);
          setSession(fresh);
        } catch (_) { /* ignore */ }
        toast.error("Couldn't continue just now — please try that again.");
      }
    } finally {
      setSending(false);
    }
  }, [session, busy, fireNoticing]);

  // STAGE TRANSITIONS — show a clear interstitial ("You've completed X. The next stage is Y.") before
  // moving forward a stage, so the learner always understands where they are going and why.
  const STAGE_DESC = {
    "Sentence Craft": "You'll work on how clearly each sentence expresses your ideas — word choice, transitions, and sentence construction.",
    "Proofreading": "You'll polish the mechanics — spelling, punctuation, capitalization, and formatting.",
  };
  const beginStage = (done, next, choice) => {
    const target = choice === "sentence_craft" ? "sentence_craft" : choice === "proofreading" ? "proofreading" : null;
    const doneKey = { "Composition": "composition", "Sentence Craft": "sentence_craft", "Proofreading": "proofreading" }[done];
    if (doneKey) recordStageSnap(doneKey);
    setViewStage(null);
    // Go DIRECTLY to the loading ("this may take a while") screen — no separate explanation screen and
    // no second press. The loading screen itself describes what the next stage does (STAGE_DESC).
    setStageTransition({ done, next, choice, target, desc: STAGE_DESC[next] || "", loading: true });
    roundChoose(choice);
  };
  // RESUME a stage the learner navigated back to (explicit — navigation alone never does this). Uses the
  // NON-DESTRUCTIVE resume endpoints: they re-open the real workspace on the current draft WITHOUT wiping
  // the stage's contract, WITHOUT a forward "Opening …" transition, and WITHOUT bouncing to Final Review.
  // NOTE: defined as a plain function (NOT useCallback) so it always calls the LATEST `roundChoose` — a
  // memoized version with empty deps would capture a stale roundChoose bound when `session` was still null
  // and silently bail at its `if (!session) return` guard.
  const resumeStage = (key) => {
    setViewStage(null);
    if (key === "composition") roundChoose("resume_composition");
    else if (key === "sentence_craft") roundChoose("resume_sentence_craft");
    else if (key === "proofreading") roundChoose("resume_proofreading");
  };
  // ZERO-ISSUE SELF-REVIEW — enter a learner-controlled mechanics review. Client-driven and immediate:
  // switch the UI right away, then persist the entered state (no LLM, no waiting on a coaching turn).
  const enterSelfReview = useCallback(() => {
    if (!session) return;
    setSelfReviewText(draft || "");
    setSelfReview(true);
    roundChoice(session.id, "self_review").then(setSession).catch(() => {});
  }, [session, draft]);
  const finishSelfReview = useCallback(() => {
    setSelfReview(false);
    setShowEpisodeReview(true);
    if (session) roundChoose("self_review_done", selfReviewText);
  }, [session, selfReviewText, roundChoose]);
  // Dismiss the transition overlay only once the NEXT stage has actually rendered.
  useEffect(() => {
    if (stageTransition?.loading && stageTransition.target && !busy && currentStage === stageTransition.target) {
      setStageTransition(null);
    }
  }, [stageTransition, busy, currentStage]);
  // Safety net — never trap the learner on the loading overlay if the turn stalls.
  useEffect(() => {
    if (!stageTransition?.loading) return;
    const t = setTimeout(() => setStageTransition(null), 120000);
    return () => clearTimeout(t);
  }, [stageTransition?.loading]);

  const wordCount = draft.trim() ? draft.trim().split(/\s+/).length : 0;

  const wide = showOT || (showWriting && !!(otData || session?.ot));
  let containerW = wide ? "max-w-5xl" : "max-w-2xl";
  const experienceActive = !!(session || otPhase || writingStarted || hasStudentTurn);
  const onEntryScreen = showStudentEntry || showAssignment;
  // Two-round "Experience The Writing Studio" teacher-facing intro gate (first screen, before assignment entry).
  const showExperienceIntro = TWO_ROUND && isStudent && !r1ContractSeen && !session && !hasStudentTurn && !otPhase;
  // Widen the container for the dialogue-centered TWO-PANE writing workspace so the writing and The Writing Studio
  // columns each get real width (narrow single-column widths are reserved for intro/entry/review screens).
  const inTwoPaneWorkspace = TWO_ROUND && !!session && !showEpisodeReview && !episodeFinished
    && !scComplete && !showAssignment && !showStudentEntry && !showExperienceIntro && !showWelcome;
  if (inTwoPaneWorkspace) containerW = "max-w-6xl";
  // Item 3 — the Writing Studio dialogue column is a sticky, viewport-bounded chat viewport (see compass-column
  // / compass-conversation below), so no JS height measurement is needed. Left as CSS-only for robustness
  // across stages (Composition, Sentence Craft, Proofreading) that render different left-column anchors.
  // Roadmap appears on the entry screen BEFORE the learner writes anything (before assignment/paragraph),
  // orienting them to the three stages. Shown once per experience; not on intro/welcome/reflection.
  const showRoadmap = !roadmapSeen && TWO_ROUND && isStudent && onEntryScreen && !showWelcome && !showExperienceIntro && !inReflection;
  // The question the learner is answering — kept pinned to the top of the screen the whole
  // time they are writing / organizing / revising (never on welcome, assignment-entry, reflection).
  const questionText = assignment || session?.assignment || "";
  const showQuestionBanner = !!questionText && !showWelcome && !onEntryScreen && !inReflection && !showOT;
  // OT PREVIEW HANDOFF → FSC. Full-screen Functionally Scaffolded Composition on the SAME session
  // (real OT thinking + provisional thesis flow through session.ot). No fixture, no re-entry.
  // WRITING PATH — Thesis-First shell (Increment 1): phases 1–2 and the transition into Build the argument.
  // Essay/Structure stay reachable quietly through "Open the essay and structure view"; existing assignments are untouched.
  // Both doors live in the Writing Path shell: Full Studio routes AND Strengthen My Writing (writing_path "strengthen_existing" / entry_mode).
  if ((["thesis_first", "ideas_first", "draft_first", "student_choice", "strengthen_existing"].includes(session?.writing_path) || session?.entry_mode === "strengthen_existing") &&
      ["get_ready", "develop_main_point", "build_argument", "write_essay", "strengthen_finish"].includes(session.writing_path_phase || "get_ready")) {
    // The Studio header stays above the Writing Path: the student can always leave the essay (it is saved and resumes next time)
    // or, for a standalone essay, set it aside and start a new one.
    // EXIT MY WRITING SESSION — save every pending keystroke, close the session on the server (it stops auto-resuming; it stays in
    // My Work / on the assignment card), forget the client pointer, then go to the student home.
    const leave = async () => {
      if (leaving) return;
      setLeaving(true);
      try { await flushAll(); await writingPathExit(session.id); } catch (_) { /* the writing is already autosaved; leaving is never blocked */ }
      try { localStorage.removeItem("compass_student_session"); } catch (e) { /* ignore */ }
      window.location.href = "/student";
    };
    const startNew = () => {
      if (!window.confirm(session.entry_mode === "strengthen_existing" ? "Set this piece aside and bring in another? Your writing is kept." : "Set this essay aside and start a new one? Your writing is kept.")) return;
      try { localStorage.removeItem("compass_student_session"); } catch (e) { /* ignore */ }
      setSession(null); setAssignment(""); setWpEntry(true);
    };
    return (
      <div className="min-h-screen bg-[#faf9f6] paper-grain">
        <header data-testid="wp-header" className="w-full max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <a href="/" data-testid="wp-home-link" title="Home" className="flex items-center gap-2 font-serif-display text-lg text-stone-800 hover:text-[#8C3A2A] transition-colors"><CompassIcon className="h-5 w-5 text-[#8C3A2A]" />The Writing Studio</a>
          <div className="flex items-center gap-5">
            {!session.assignment_id && <button data-testid="wp-new-essay" onClick={startNew} className="text-[11px] font-mono-panel uppercase tracking-[0.14em] text-stone-400 hover:text-[#8C3A2A] transition-colors">{session.entry_mode === "strengthen_existing" ? "New piece" : "New essay"}</button>}
            <button data-testid="wp-exit" onClick={leave} disabled={leaving} title="Saves your writing and closes this session" className="inline-flex items-center gap-1.5 text-[11px] font-mono-panel uppercase tracking-[0.14em] text-stone-400 hover:text-[#8C3A2A] transition-colors disabled:opacity-50"><X className="h-3.5 w-3.5" /> {leaving ? "Saving…" : "Exit my writing session"}</button>
            <AccountMenu onOpenMyWork={() => setShowMyWork(true)} onAuthSuccess={handleAuthSuccess} />
          </div>
        </header>
        {showMyWork
          ? <div className="w-full max-w-5xl mx-auto px-6"><MyWork onBack={() => setShowMyWork(false)} onResume={resumeSessionById} onOpenReview={openReviewById} /></div>
          : <ThesisFirstShell session={session} onSession={setSession} onOpenStudio={() => setStudioOverride(true)} />}
      </div>
    );
  }
  // A signed-in student never lands in the legacy Studio: resolving → nothing yet; no Writing-Path session → the Writing-Path entry.
  if (isStudent && user && !session?.writing_path && wpResolving) {
    return <div data-testid="wp-resolving" className="min-h-screen bg-[#faf9f6] paper-grain flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-stone-400" /></div>;
  }
  if (isStudent && user && !session?.writing_path && wpChoice) {
    const strengthenDoor = mode === "writing";
    const when = wpChoice.updated_at ? new Date(wpChoice.updated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
    return (
      <div className="min-h-screen bg-[#faf9f6] paper-grain">
        <header data-testid="wp-header" className="w-full max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <a href="/" data-testid="wp-home-link" title="Home" className="flex items-center gap-2 font-serif-display text-lg text-stone-800 hover:text-[#8C3A2A] transition-colors"><CompassIcon className="h-5 w-5 text-[#8C3A2A]" />The Writing Studio</a>
          <div className="flex items-center gap-5">
            <button data-testid="wp-exit" onClick={() => { window.location.href = "/student"; }} className="inline-flex items-center gap-1.5 text-[11px] font-mono-panel uppercase tracking-[0.14em] text-stone-400 hover:text-[#8C3A2A] transition-colors"><X className="h-3.5 w-3.5" /> Back to my home</button>
            <AccountMenu onOpenMyWork={() => setShowMyWork(true)} onAuthSuccess={handleAuthSuccess} />
          </div>
        </header>
        {showMyWork ? <div className="w-full max-w-5xl mx-auto px-6"><MyWork onBack={() => setShowMyWork(false)} onResume={resumeSessionById} onOpenReview={openReviewById} /></div> : (
        <main data-testid="wp-choice" className="w-full max-w-3xl mx-auto px-6 py-14">
          <p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A]">{strengthenDoor ? "Strengthen My Writing" : "Full Writing Studio"}</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900 mt-3">You have {strengthenDoor ? "a piece" : "an essay"} in progress.</h1>
          <p className="text-[15px] text-stone-600 mt-3 max-w-xl">Your work is saved and stays in <span className="text-stone-800">My Work</span> either way. Pick up where you left off, or begin something new.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 max-w-2xl">
            <button data-testid="wp-choice-continue" onClick={continueChoice} className="text-left border border-stone-300 bg-white rounded-sm p-5 hover:border-[#8C3A2A] transition-colors">
              <p className="font-serif-display text-xl text-stone-900">Continue</p>
              <p data-testid="wp-choice-title" className="mt-2 text-[14px] text-stone-700 line-clamp-3">{wpChoice.assignment_title || wpChoice.assignment || (strengthenDoor ? "Your piece in progress" : "Your essay in progress")}</p>
              {when && <p className="mt-2 text-[12px] text-stone-400">Last worked {when}</p>}
            </button>
            <button data-testid="wp-choice-new" onClick={() => { try { localStorage.removeItem("compass_student_session"); } catch (e) { /* ignore */ } setWpChoice(null); setWpEntry(true); }} className="text-left border border-stone-300 bg-white rounded-sm p-5 hover:border-[#8C3A2A] transition-colors">
              <p className="font-serif-display text-xl text-stone-900">{strengthenDoor ? "Bring in another piece" : "Start a new essay"}</p>
              <p className="mt-2 text-[14px] text-stone-700">{strengthenDoor ? "Type in different writing to strengthen." : "Begin a fresh essay from the assignment or question you are working on."}</p>
              <p className="mt-2 text-[12px] text-stone-400">The one in progress is kept in My Work.</p>
            </button>
          </div>
        </main>)}
      </div>
    );
  }
  if (isStudent && user && !session?.writing_path && wpEntry) {
    return (
      <div className="min-h-screen bg-[#faf9f6] paper-grain">
        <header data-testid="wp-header" className="w-full max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <a href="/" data-testid="wp-home-link" title="Home" className="flex items-center gap-2 font-serif-display text-lg text-stone-800 hover:text-[#8C3A2A] transition-colors"><CompassIcon className="h-5 w-5 text-[#8C3A2A]" />The Writing Studio</a>
          <div className="flex items-center gap-5">
            <button data-testid="wp-exit" onClick={() => { try { localStorage.removeItem("compass_student_session"); } catch (e) { /* ignore */ } window.location.href = "/student"; }} className="inline-flex items-center gap-1.5 text-[11px] font-mono-panel uppercase tracking-[0.14em] text-stone-400 hover:text-[#8C3A2A] transition-colors"><X className="h-3.5 w-3.5" /> Exit my writing session</button>
            <AccountMenu onOpenMyWork={() => setShowMyWork(true)} onAuthSuccess={handleAuthSuccess} />
          </div>
        </header>
        {showMyWork ? <div className="w-full max-w-5xl mx-auto px-6"><MyWork onBack={() => setShowMyWork(false)} onResume={resumeSessionById} onOpenReview={openReviewById} /></div> : mode === "writing" ? (
        <StrengthenWritingEntry onStart={async (prompt, text) => {
          const r = await cStrengthenStart(prompt, text);
          const s = await getSession(r.session_id);
          try { localStorage.setItem("compass_student_session", JSON.stringify({ id: s.id, mode, classroom: false })); } catch (e) { /* ignore */ }
          feedbackEvent(s.id, "resume", {});
          setAssignment(s.assignment || "");
          setSession(s);
        }} />) : (
        <StandaloneWritingPathEntry onStart={async (text) => {
      const r = await cStandaloneStart(text);
      const s = await getSession(r.session_id);
      try { localStorage.setItem("compass_student_session", JSON.stringify({ id: s.id, mode, classroom: false })); } catch (e) { /* ignore */ }
      feedbackEvent(s.id, "resume", {});
      setAssignment(s.assignment || "");
      setSession(s);
    }} />)}
      </div>
    );
  }
  if (fscMode && session?.id) {
    return (
      <div className="relative">
        <div className="mx-auto w-full max-w-6xl px-6 pt-16"><OrientationLayer signals={{ ...orientationSignals, screen: "fsc" }} /></div>
        <div className="absolute top-4 left-4 z-50">
          <BackControl onClick={() => { setFscMode(false); setOtPhase(true); }} />
        </div>
        <FunctionallyScaffoldedComposition sessionId={session.id}
          onContinueToComposition={(draftText) => {
        // FSC hands the assembled draft UP to the ESSAY-LEVEL CONTROLLER (whole-first), not straight
        // into the local writing box. The controller reconstructs the whole and surfaces one dominant
        // move; when it routes to local composition it lands the draft in the existing WritingScreen.
        if (draftText) { setResponse(draftText); setDraft(draftText); setEssayDraft(draftText); }
        setFscMode(false);
        setEssayMode(true);
      }} />
      </div>
    );
  }
  // ESSAY LEVEL COMPOSITION CONTROLLER — whole-first entry to Composition (full pathway from FSC, or a
  // multi-paragraph Composition-Only draft). Routes DOWN to the local engine (WritingScreen) when a
  // problem is local / on completion; the draft is preserved throughout.
  if (essayMode && session?.id) {
    return (<>
      <div className="mx-auto w-full max-w-6xl px-6 pt-4"><OrientationLayer signals={{ ...orientationSignals, screen: "essay" }} /></div>
      <EssayComposition sessionId={session.id} initialDraft={mode === "ot" ? "" : (essayDraft || response)} fromFsc={mode === "ot"}
      onReturnToThinking={(purpose) => { setRecursionPurpose(purpose || null); setEssayMode(false); setOtPhase(true); }}
      onLocalComposition={(finalDraft) => {
        if (finalDraft) { setResponse(finalDraft); setDraft(finalDraft); }
        setEssayMode(false);
        setEssayReviewed(true);
        setWritingStarted(true);
        if (mode === "ot") setCompositionArrival(true);  // full-path convergence transition
      }}
      onBack={(fromFscBack) => {
        setEssayMode(false);
        if (fromFscBack) { setOtPhase(true); }  // Increment C: back from Composition returns to the unified workspace
        else { setWritingStarted(true); }
      }} />
    </>);
  }
  // PHASE 2, CHECKPOINT 1 — FUNCTIONAL ORGANIZATION teaching stage (between OT hand-off and Composition).
  // Multi-turn FO coach scaffolds thinking -> writing-for-a-reader; on completion enters the composition bridge.
  if (foPhase && session?.id) {
    return (<>
      <div className="mx-auto w-full max-w-6xl px-6 pt-4"><OrientationLayer signals={{ ...orientationSignals, screen: "fo" }} /></div>
      <FunctionalOrganization
        sessionId={session.id}
        onComplete={(transitionMessage) => finishFO(transitionMessage)}
        onBack={() => { setFoPhase(false); setOtPhase(true); }}
        onStepBackToOT={(ret) => initiateReturn({ from_process: "fo", target: { ...ret, stage: "OT" }, from_object: { label: "this part" } })}
        onResumeToComposition={doResume}
        entryConfirmation={returnConfirmation}
      />
    </>);
  }
  // FULL-PATH CONVERGENCE TRANSITION — "You've reached Composition" (brief; no tutorial, no FSC recap).
  // Shown once, only for students who arrived via the full OT/FSC pathway. [Continue] reveals the shared
  // Composition writing box (WritingScreen) with everything they've written already present.
  if (compositionArrival && session?.id) {
    return (
      <div className="min-h-screen paper-grain flex items-center justify-center px-6" data-testid="composition-arrival">
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-lg text-center"
        >
          <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-4" data-testid="composition-arrival-kicker">
            You&rsquo;ve reached Composition
          </p>
          <h1 className="font-serif-display text-3xl sm:text-4xl leading-tight text-stone-900">
            Now develop your draft for your reader
          </h1>
          <p className="text-stone-600 mt-5 text-[15.5px] leading-relaxed">
            You&rsquo;ve developed and organized your thinking and begun turning it into a draft. Now The Writing
            Studio will help you develop that draft as writing for your reader.
            <span className="block mt-2 text-stone-500">Everything you&rsquo;ve written is already here.</span>
          </p>
          <button
            onClick={() => setCompositionArrival(false)}
            data-testid="composition-arrival-continue"
            className="group mt-8 inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-8 py-3.5 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] hover:-translate-y-px transition-[background-color,transform]"
          >
            Continue
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    );
  }
  return (
    <div className="min-h-screen paper-grain flex flex-col items-center">
      {showRoadmap && <SessionRoadmap onStart={() => setRoadmapSeen(true)} />}
      {/* STAGE TRANSITION — go straight to the loading ("this may take a while") screen; it names what
          the next stage does. No separate explanation screen / second press. */}
      {stageTransition && (
        <div data-testid="stage-transition" className="fixed inset-0 z-50 flex items-center justify-center bg-[#faf9f6]/95 backdrop-blur-sm px-6">
          <div className="w-full max-w-md bg-white border border-stone-200 rounded-md shadow-xl p-8 text-center">
            <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-[#8C3A2A]/[0.08]">
              <Loader2 className="h-5 w-5 text-[#8C3A2A] animate-spin" />
            </div>
            <p className="font-mono-panel text-[11px] uppercase tracking-[0.2em] text-stone-400 mb-1">Please wait</p>
            <h2 className="font-serif-display text-[1.5rem] leading-tight text-stone-900 mb-2">
              Opening {stageTransition.next}…
            </h2>
            {stageTransition.desc && (
              <p className="text-[14px] text-stone-700 mb-2" data-testid="stage-transition-desc">
                {stageTransition.desc}
              </p>
            )}
            <p className="text-[13.5px] text-stone-500" data-testid="stage-transition-loading">
              This can take a little while — {stageTransition.next} will open automatically when it's ready.
            </p>
          </div>
        </div>
      )}
      {/* Container-local "new response" cue lives INSIDE the conversation viewport (see below), shown only
          when the learner has intentionally scrolled up to reread history. No document-level scrolling. */}
      {session?.sentence_craft_test_entry && (
        <div
          data-testid="sc-test-banner"
          style={{ position: "fixed", top: 12, right: 12, zIndex: 70 }}
          className="flex items-center gap-2 rounded-full bg-[#1c1917] text-[#e7e5e4] pl-3 pr-1.5 py-1 shadow-lg border border-[#57534e]"
        >
          <span className="text-[10px] uppercase tracking-[0.16em] font-mono-panel text-[#e0a89a]">
            SC Test Mode{session?.sc_test_thesis_inferred ? " · inferred thesis" : ""}
          </span>
          <button
            data-testid="sc-test-new-button"
            onClick={() => {
              try { localStorage.removeItem("compass_student_session"); } catch (e) { /* ignore */ }
              window.location.href = "?sctest";
            }}
            className="rounded-full bg-[#8C3A2A] text-white text-[11px] px-2.5 py-1 hover:bg-[#75301f] transition-colors"
          >
            New SC Test
          </button>
        </div>
      )}
      {!showWelcome && (
        <header className={`w-full ${containerW} flex items-center justify-between px-6 py-5`}>
          <a href="/" data-testid="studio-home-link" title="Home" className="flex items-center gap-2 font-serif-display text-lg text-stone-800 hover:text-[#8C3A2A] transition-colors">
            <CompassIcon className="h-5 w-5 text-[#8C3A2A]" />
            The Writing Studio
          </a>
          <div className="flex items-center gap-5">
            {isStudent && experienceActive && !inReflection && (
              <button
                onClick={() => setEarlyExitOpen(true)}
                data-testid="exit-experience-button"
                className="inline-flex items-center gap-1.5 text-[11px] font-mono-panel uppercase tracking-[0.14em] text-stone-400 hover:text-[#8C3A2A] transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Exit my writing session
              </button>
            )}
            <AccountMenu
              onOpenMyWork={() => setShowMyWork(true)}
              onAuthSuccess={handleAuthSuccess}
            />
          </div>
        </header>
      )}

      <main className={`w-full ${containerW} flex-1 flex flex-col px-6 pb-12`}>
        {showMyWork && (
          <div
            data-testid="my-work-overlay"
            className="fixed inset-0 z-40 bg-[#faf9f6] overflow-y-auto px-6"
          >
            <div className={`w-full ${containerW}`}>
              <MyWork
                onBack={() => setShowMyWork(false)}
                onResume={resumeSessionById}
                onOpenReview={openReviewById}
              />
            </div>
          </div>
        )}
        {showQuestionBanner && (
          <div
            data-testid="assignment-banner"
            className="sticky top-0 z-20 -mx-6 px-6 py-2.5 bg-[#faf9f6]/95 backdrop-blur-sm border-b border-stone-200 flex items-baseline gap-3"
          >
            <p className="shrink-0 font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400">
              Assignment
            </p>
            <p
              data-testid="assignment-banner-text"
              className="min-w-0 flex-1 truncate text-[14px] leading-snug text-stone-700 font-serif-display"
              title={questionText}
            >
              {questionText}
            </p>
          </div>
        )}
        {!onEntryScreen && session?.id && !showOT && (inTwoPaneWorkspace || writingStarted || essayReviewed || showEpisodeReview || episodeFinished || scActive || proofreadingActive) && (
          <OrientationLayer signals={orientationSignals} onSendToCoach={orientSendToCoach} difficulty={difficulty} onDifficultyDone={difficultyDone} className="mb-2" />
        )}
        {onEntryScreen && !showStudentEntry && !showExperienceIntro && <WelcomeIntro />}
        {(showEpisodeReview || episodeFinished) && session?.id && !viewingPast ? (
          <>
            <h1
              data-testid="final-review-title"
              className="mt-2 mb-4 text-center font-serif-display text-4xl sm:text-5xl text-stone-900"
            >
              Final Review
            </h1>
            {!isAuthed && (
              <div
                data-testid="save-work-prompt"
                className="mb-4 mx-auto max-w-xl flex flex-col items-center text-center gap-2 rounded-sm border border-[#8C3A2A]/20 bg-[#8C3A2A]/[0.04] px-4 py-4"
              >
                <p className="text-[13px] text-stone-600">
                  Want to keep this? <span className="text-stone-800 font-medium">Create a free account</span> to save your paragraph and review.
                </p>
                <button
                  onClick={() => setSavePromptOpen(true)}
                  data-testid="save-work-signin-button"
                  className="inline-flex items-center gap-1.5 text-[12.5px] px-4 py-1.5 rounded-sm bg-[#8C3A2A] text-white hover:bg-[#6B2C20] transition-colors"
                >
                  Sign in to save
                </button>
              </div>
            )}
            <StageStepper
              current="final"
              reached={reachedStageKeys}
              stale={staleStageKeys}
              viewing={viewStage}
              onNavigate={navigateStage}
            />
            <EpisodeReview sessionId={session.id} onBack={() => { setShowEpisodeReview(false); restart(); }} onNewSession={() => { setShowEpisodeReview(false); restart(); }} onContinueSentenceCraft={() => { setShowEpisodeReview(false); roundChoose("sentence_craft"); }} />
          </>
        ) : (selfReview && !viewingPast) ? (
          <SelfReviewScreen
            draft={selfReviewText}
            onChange={setSelfReviewText}
            busy={busy}
            onDone={finishSelfReview}
          />
        ) : (scComplete && !proofreadingActive && !viewingPast) ? (
          <SCCompleteScreen
            categories={proofreadingOffer?.categories || []}
            noIssues={proofreadingOffer?.no_issues === true}
            remaining={proofreadingOffer?.sc_remaining || []}
            busy={busy}
            onKeepGoing={() => roundChoose("keep_going")}
            onProofread={() => beginStage("Sentence Craft", "Proofreading", "proofreading")}
            onSelfReview={enterSelfReview}
            onSkip={() => roundChoose("skip_proofreading")}
          />
        ) : showExperienceIntro ? (
          <div className="flex-1 flex flex-col justify-center py-10 max-w-2xl min-h-[70vh]" data-testid="round1-contract">
            <div data-testid="intro-screen">
              <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-8">
                Experience The Writing Studio
              </p>
              <h1 className="font-serif-display text-[2.4rem] sm:text-[3.1rem] leading-[1.1] text-stone-900 max-w-[20ch]">
                AI that helps students learn <span className="text-[#8C3A2A]">through</span> writing — not skip it.
              </h1>
              <div className="mt-8 space-y-4 text-[16px] leading-relaxed text-stone-700 max-w-[56ch]">
                <p>
                  Most AI helps students <span className="text-stone-900 font-medium">complete</span> writing.
                  The Writing Studio is designed to help them <span className="text-stone-900 font-medium">learn through</span> writing.
                </p>
                <p>
                  It never writes or rewrites the paper for you. It helps you develop and organize your ideas,
                  shape them into writing for a reader, and strengthen your writing one manageable step at a time.
                </p>
                <p>
                  Spend about 10 minutes using The Writing Studio as a student — give it a real assignment and a real paragraph.
                </p>
              </div>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-[58ch]">
                <button
                  onClick={() => { window.location.search = "?studio=full"; }}
                  data-testid="round1-contract-begin"
                  className="group flex flex-col items-start text-left rounded-sm border border-[#8C3A2A] bg-[#8C3A2A] text-white px-5 py-4 hover:bg-[#6B2C20] hover:-translate-y-px transition-[background-color,transform] focus:outline-none focus:ring-2 focus:ring-[#8C3A2A] focus:ring-offset-2"
                >
                  <span className="inline-flex items-center gap-2 font-medium tracking-wide uppercase text-[13px]">
                    The Full Studio
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="mt-1.5 text-[12.5px] leading-snug text-white/85 font-normal">
                    Start from your first ideas — organize your thinking, shape your essay, then refine it.
                  </span>
                </button>
                <button
                  onClick={() => { window.location.search = "?studio=composition"; }}
                  data-testid="experience-start-composing"
                  className="group flex flex-col items-start text-left rounded-sm border border-stone-300 bg-white px-5 py-4 hover:border-[#8C3A2A] hover:-translate-y-px transition-[border-color,transform] focus:outline-none focus:ring-2 focus:ring-[#8C3A2A] focus:ring-offset-2"
                >
                  <span className="inline-flex items-center gap-2 font-medium tracking-wide uppercase text-[13px] text-[#8C3A2A]">
                    Start by Composing
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="mt-1.5 text-[12.5px] leading-snug text-stone-500 font-normal">
                    Already have thinking you&rsquo;re ready to write? Jump straight into composing and refining.
                  </span>
                </button>
              </div>
            </div>
          </div>
        ) : showWelcome ? (
          <WelcomeScreen onBegin={() => setEntered(true)} />
        ) : showAssignment ? (
          <AssignmentScreen
            assignment={assignment}
            setAssignment={setAssignment}
            onContinue={() => setWritingStarted(true)}
            onBack={() => setEntered(false)}
          />
        ) : showStudentEntry ? (
          <StudentEntry
            mode={mode}
            initialAssignment={assignment}
            onSubmit={enterStudent}
            submitting={starting}
          />
        ) : showOT ? (
          <ThinkingWorkspace
            sessionId={session?.id}
            initialOt={otData}
            onComplete={finishOrganizing}
            returnPurpose={recursionPurpose}
            onResume={async () => {
              if (returnActive) { await doResume(); return; }  // Checkpoint 2 — cascade return-and-resume
              try { await essayResumeComposition(session.id); } catch (e) { /* ignore */ }
              setRecursionPurpose(null); setOtPhase(false); setEssayMode(true);
            }}
          />
        ) : showWriting ? (
          <WritingScreen
            ot={otData || session?.ot}
            directCompose={mode === "writing"}
            hidePlanning={mode === "ot"}
            response={response}
            setResponse={setResponse}
            onEditCapture={captureDraftEdit}
            onPasteCapture={capturePaste}
            onDropCapture={blockDrop}
            onSubmit={submitResponse}
            onBack={() => {
              setWritingStarted(false);
              if (mode === "ot" && session) setOtPhase(true);
              else if (isStudent) setSession(null);
            }}
            submitting={starting}
          />
        ) : inReflection ? (
          reviewingAsTeacher ? (
            <TeacherReflection
              sessionId={session?.id}
              onBack={() => setReviewingAsTeacher(false)}
            />
          ) : (
            <ExperienceReflection
              reflection={session?.experience_control?.reflection}
              draft={draft}
              onRestart={restart}
              onReviewAsTeacher={isTeacher ? () => setReviewingAsTeacher(true) : undefined}
            />
          )
        ) : (
          <div className="flex-1 flex flex-col py-4">
            {/* Back sits on the same line as the "Your progress" label, aligned left. */}
            <div className="relative" data-testid="progress-nav-row">
              <div className="absolute left-0 top-2 z-10"><BackControl onClick={goBackStage} /></div>
              <StageStepper current={currentStage} reached={reachedStageKeys} stale={staleStageKeys} viewing={viewStage} onNavigate={navigateStage} />
            </div>

            {/* Within-section progress moved BELOW the writing box (left column). */}

            {/* DIALOGUE-CENTERED TWO-PANE: LEFT = writing / active instructional object + structured
                displays + controls; RIGHT = persistent The Writing Studio dialogue (the primary instructional
                medium). Stacks to one column on mobile/tablet. */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(340px,40%)] lg:gap-6 lg:items-start">
              <div className="min-w-0 flex flex-col" data-testid="writing-column">

            {/* "COMPOSITION — Develop your writing for your reader" header removed here: the stage stepper
                already marks Composition; the writing, the map and the dialogue are the three things on screen. */}

            {viewingPast && (
              <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.03] px-4 py-3" data-testid="stage-view-banner">
                <span className="text-[13px] text-stone-700">
                  You&rsquo;re looking back at <span className="font-semibold text-stone-900">{STAGE_LABEL[viewStage]}</span>. Edit freely &mdash; your coach won&rsquo;t restart on its own.
                </span>
                <button
                  onClick={() => resumeStage(viewStage)}
                  disabled={busy}
                  data-testid="stage-resume-btn"
                  className="text-[12px] font-medium text-[#8C3A2A] border border-[#8C3A2A]/40 px-3 py-1.5 rounded-full hover:bg-[#8C3A2A] hover:text-white transition-colors disabled:opacity-40"
                >
                  Resume {STAGE_LABEL[viewStage]}
                </button>
                <button
                  onClick={() => setViewStage(null)}
                  data-testid="stage-view-back-btn"
                  className="text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2"
                >
                  {(showEpisodeReview || episodeFinished) ? "Back to Final Review" : "Back to where I was"}
                </button>
              </div>
            )}

            {/* Structural diagram relocated UNDER the dialogue box (right column). */}

            {/* The passage — document canvas, editable in place. A transparent-text
                overlay sits behind the textarea to tint the recognized thesis. */}
            <div className="relative bg-white border border-stone-300 rounded-sm">
              <div
                ref={highlightRef}
                aria-hidden="true"
                data-testid="preview-document-highlight"
                className="absolute inset-0 overflow-hidden pointer-events-none px-7 sm:px-10 py-8 text-[17px] leading-9 font-serif-display whitespace-pre-wrap break-words text-transparent"
              >
                {renderInterpretationSegments(draft, viewingPast ? [] : renderRegions, viewingPast ? [] : renderPortionRanges, renderFocusClass)}
                {"\n"}
              </div>
              <textarea
                data-testid="preview-document"
                ref={docRef}
                value={draft}
                onChange={(e) => { captureDraftEdit(); setDraft(e.target.value); }}
                onPaste={capturePaste}
                onDrop={blockDrop}
                onScroll={() => {
                  if (highlightRef.current && docRef.current)
                    highlightRef.current.scrollTop = docRef.current.scrollTop;
                }}
                placeholder="Your response…"
                className="relative block w-full min-h-[34vh] bg-transparent px-7 sm:px-10 py-8 text-[17px] leading-9 text-stone-900 placeholder:text-stone-400 outline-none resize-none custom-scroll font-serif-display whitespace-pre-wrap break-words"
              />
            </div>

            {/* CURRENT PASSAGE (7A) — the ONE sentence Sentence Craft is coaching, surfaced right here in
                the work area (not only highlighted somewhere above). Derived from the SAME canonical
                sc.active_sentence_text located against the live draft; editing it splices back into the
                full paragraph. If it can't be located we still surface the canonical text. */}
            {scActive && !viewingPast && activeSentenceLive.trim() && (
              <div className="mt-4 rounded-md border border-[#8C3A2A]/30 bg-white" data-testid="active-passage">
                <div className="flex items-center justify-between px-4 pt-3">
                  <span className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A]">
                    Current passage{sc?.sentence_count ? ` \u00b7 sentence ${(sc.active_sentence_index ?? 0) + 1} of ${sc.sentence_count}` : ""}
                  </span>
                  <div className="flex items-center gap-2">
                    {!activeScRange && (
                      <span className="text-[10px] text-amber-600" data-testid="active-passage-unlocated">from your latest analyzed draft</span>
                    )}
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setFocusPassageOpen(true)}
                            data-testid="focus-passage-open"
                            className="inline-flex items-center gap-1 text-[10px] font-mono-panel uppercase tracking-[0.12em] text-[#8C3A2A] border border-[#8C3A2A]/30 rounded-full px-2 py-1 hover:bg-[#8C3A2A] hover:text-white transition-colors"
                          >
                            <Maximize2 className="h-3 w-3" /> Focus
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[250px] bg-stone-900 text-white text-[12px] leading-snug">
                          Opens just the sentence The Writing Studio is coaching in a small movable box, so you can revise it on its own. Your edits still update the full paragraph.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                <textarea
                  data-testid="active-passage-input"
                  value={activeSentenceLive}
                  onChange={(e) => spliceActiveSentence(e.target.value)}
                  disabled={!activeScRange || busy}
                  rows={2}
                  className="mt-1 block w-full bg-transparent px-4 pb-2 text-[16px] leading-8 text-stone-900 outline-none resize-none font-serif-display"
                />
                <p className="px-4 pb-3 text-[11px] text-stone-400">
                  This is the exact sentence The Writing Studio is coaching. Editing it updates your full paragraph above.
                </p>
              </div>
            )}

            {/* PRIMARY ACTION — adjacent to the active editing surface (item 6). */}
            {(!scComplete || proofreadingActive) && !showScHandoff && !viewingPast && (
              <div className="mt-5 flex items-center justify-between gap-3" data-testid="left-action-bar">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-500 font-mono-panel" data-testid="preview-word-count">
                    {wordCount} words
                  </span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setScratchOpen((v) => !v)}
                          data-testid="scratch-toggle"
                          className={"inline-flex w-fit items-center gap-1.5 text-[10px] font-mono-panel uppercase tracking-[0.12em] border rounded-full px-2.5 py-1 transition-colors " + (scratchOpen ? "bg-[#8C3A2A] text-white border-[#8C3A2A]" : "text-stone-600 border-stone-300 hover:border-[#8C3A2A] hover:text-[#8C3A2A]")}
                        >
                          <StickyNote className="h-3 w-3" /> Scratch
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[250px] bg-stone-900 text-white text-[12px] leading-snug">
                        A private space to jot notes, plans, or half-formed ideas as you write. It isn't graded and isn't sent to The Writing Studio.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {focusAvailable && !focusPassageOpen && (
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setFocusPassageOpen(true)}
                            data-testid="focus-passage-reopen"
                            className="inline-flex w-fit items-center gap-1.5 text-[10px] font-mono-panel uppercase tracking-[0.12em] text-[#8C3A2A] border border-[#8C3A2A]/30 rounded-full px-2.5 py-1 hover:bg-[#8C3A2A] hover:text-white transition-colors"
                          >
                            <Maximize2 className="h-3 w-3" /> Focus
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[250px] bg-stone-900 text-white text-[12px] leading-snug">
                          Opens just the sentence(s) The Writing Studio is coaching in a small movable box, so you can work on them without the rest of the paragraph in the way. Your edits still update the full paragraph.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <button
                  onClick={sendRevision}
                  data-testid="preview-send-revision"
                  disabled={!draft.trim() || busy || !dirty}
                  className="group inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-6 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Reading&hellip;
                    </>
                  ) : (
                    <>
                      {proofreadingActive ? (bridgeMulti ? "Send corrected writing" : "Send corrected paragraph") : "Send revision"}
                      <ArrowRight className="h-4 w-4 transition-transform group-enabled:group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </div>
            )}
            {scratchOpen && session?.id && !viewingPast && (
              <div className="mt-3" data-testid="scratch-space-column">
                <ScratchSpace storageKey={`compass_scratch_${session.id}`} className="" defaultOpen hideHeader />
              </div>
            )}

            {/* §22 CASCADE ORIENTATION — current focal process across the developmental cascade.
                COMPOSITION MINIMALISM: hidden while composing (Round 1) — the writing, the Paragraph Function
                Map and the dialogue are the only three things on screen. Still shown in SC / Proofreading. */}
            {!viewingPast && episode?.active_round && !(episode.active_round === 1 && !scActive && !proofreadingActive) ? (
              <CascadeOrientation
                sessionId={session?.id}
                view={proofreadingActive ? "proofread" : ""}
                refreshKey={`${bridgeCursor?.index}-${episode?.active_round}-${scActive}-${proofreadingActive}`}
                className="mt-5"
                onStepBack={(ret) => initiateReturn({
                  from_process: "composition",
                  target: ret,
                  from_object: { label: bridgeMulti ? `part ${(bridgeCursor?.index ?? 0) + 1} of your writing` : "your writing",
                                 part_key: ret?.part_key ?? null, cursor: bridgeCursor?.index ?? null, draft },
                })}
              />
            ) : null}

            {/* P5 — WHOLE-ESSAY ORIENTATION: the student keeps the whole essay in view while composing one
                paragraph at a time (section-based bridge). Falls back to the simple part indicator for a
                bridge without a section outline. */}
            {!viewingPast && episode?.active_round === 1 && !proofreadingActive && !scActive
              && bridgeCursor?.outline?.length > 0 && (bridgeCursor.outline.length > 1 || bridgeCursor.total > 1) && (
              <EssayOrientation cursor={bridgeCursor} />
            )}
            {!viewingPast && bridgeMulti && episode?.active_round === 1 && !proofreadingActive && !scActive
              && !(bridgeCursor?.outline?.length > 0) && (
              <div className="mt-5 rounded-md border border-stone-200 bg-[#faf9f6] px-4 py-2.5 text-[12.5px] text-stone-600" data-testid="composition-part-indicator">
                Working closely on <span className="font-semibold text-stone-800">part {bridgeCursor.index + 1} of {bridgeCursor.total}</span> of your writing.
              </div>
            )}
            {/* COMPOSITION PROGRESS MONITOR — removed from the Composition view (COMPOSITION MINIMALISM): its
                "working now / next" text repeated the map and the dialogue. The Paragraph Function Map is the one
                place to look for structure. (Component kept for the developer panel / future opt-in.) */}
            {/* PARAGRAPH FUNCTION MAP (How your paragraph is working: five functions with what each is doing
                + YOU ARE HERE) + Reader Model — left writing column, under the Progress monitor. The map
                component carries its own card border. */}
            {!viewingPast && hasStructuralMap && (
              <div ref={hypiwRef} className="mt-5" data-testid="structural-strip">
                <ParagraphMap map={vmMap} operation={activeCoaching?.instructional_operation} focusNote={activeCoaching?.focus_description} isMulti={bridgeMulti} />
              </div>
            )}
            {!viewingPast && scActive && scProgress && (
              <div ref={hypiwRef} className="mt-5" data-testid="structural-strip">
                <SentenceCraftProgress progress={scProgress} complete={false} />
              </div>
            )}


            {/* WHAT NEXT + section controls — under the HYPIW box (left column). */}
            {/* TWO-ROUND bounded experience — episode contract/progress + Round-1 landing choice. */}
            {TWO_ROUND && episode && (
              <div className="mt-5 space-y-3" data-testid="two-round-panel">
                {/* Round progress — the bounded HORIZON, not a numeric counter. Never a 0-of-0 state. */}
                {episode.active_round === 1 && !proofreadingActive && ["WORKING", "CLOSING"].includes(episode.round1?.state) &&
                 ((episode.round1?.completed || []).length > 0 || episode.round1?.current_priority ||
                  episode.round1?.visual_map?.map_state) && (() => {
                  const done = episode.round1?.completed || [];
                  const current = episode.round1?.current_priority || "";
                  const next = episode.round1?.next_step || "";
                  const working = episode.round1?.state === "WORKING";
                  const reading = episode.round1?.current_reading || null;
                  const vm = episode.round1?.visual_map || null;
                  const hasMap = working && vm && vm.map_state;
                  const hasReading = working && reading && (reading.paragraph_job || (reading.where_you_are || []).length);
                  // Structural map is now rendered in the LEFT column (above the writing box); the
                  // "Worked through" chips are in the top within-section meter. Nothing to render here.
                  void hasMap; void hasReading;
                  return null;
                })()}

                {/* Full option set (Skip to Proofreading / Finish) — shown only if the learner opted to
                    keep refining; otherwise the dominant Sentence-Craft handoff panel below is primary. */}
                {scOfferPending && !busy && keepRefining && (
                  <div className="rounded-md border border-[#8C3A2A]/30 bg-[#8C3A2A]/[0.04] p-5" data-testid="round-choice-panel">
                    <p className="font-serif-display text-lg text-stone-900 mb-1">Want to work on your sentences too?</p>
                    <p className="text-[13.5px] text-stone-600 mb-4">
                      You've completed your writing-development session. If you'd like, you can spend about 10 more
                      minutes on Sentence Craft — where you improve how clearly each sentence expresses your ideas
                      (word choice, transitions, and sentence construction).
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => beginStage("Composition", "Sentence Craft", "sentence_craft")}
                        disabled={busy}
                        data-testid="round-choice-sentence-craft"
                        className="inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-5 py-2.5 rounded-sm font-medium hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
                      >
                        Continue to Sentence Craft
                      </button>
                      <button
                        onClick={() => beginStage("Composition", "Proofreading", "proofreading")}
                        disabled={busy}
                        data-testid="round-choice-proofreading"
                        className="inline-flex items-center gap-2 border border-stone-300 text-stone-700 px-5 py-2.5 rounded-sm hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                      >
                        Skip Sentence Craft and Go Straight to Proofreading
                      </button>
                      <button
                        onClick={() => roundChoose("finish")}
                        disabled={busy}
                        data-testid="round-choice-finish"
                        className="inline-flex items-center gap-2 border border-stone-300 text-stone-700 px-5 py-2.5 rounded-sm hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                      >
                        Finish & see my review
                      </button>
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* SENTENCE CRAFT within-section progress relocated to the top within-section meter. */}

            {/* LEARNER CONTROL over how much to do (§4). Stopping is never treated as failure. */}
            {scActive && (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="sc-controls">
                <span className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400 mr-1">
                  You decide
                </span>
                <button
                  onClick={() => scSetControl("keep")}
                  disabled={busy}
                  data-testid="sc-control-keep"
                  className="text-[12px] border border-stone-300 text-stone-700 px-3 py-1.5 rounded-full hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                >
                  Keep working
                </button>
                <button
                  onClick={() => scSetControl("finish_after_this")}
                  disabled={busy}
                  data-testid="sc-control-finish-after-this"
                  className="text-[12px] border border-stone-300 text-stone-700 px-3 py-1.5 rounded-full hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                >
                  Finish after this one
                </button>
                <button
                  onClick={() => scSetControl("stop")}
                  disabled={busy}
                  data-testid="sc-control-stop"
                  className="text-[12px] border border-stone-300 text-stone-700 px-3 py-1.5 rounded-full hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                >
                  I'm ready to stop
                </button>
              </div>
            )}

            {/* SENTENCE-CRAFT SAFETY NET — a GUARANTEED forward path. If the learner is in Sentence Craft
                (round 2) but there is no active target to coach, no completion panel, and no proofreading
                step showing, they must never be stranded: always offer an explicit way to the Final Review. */}
            {episode?.active_round === 2 && !scActive && !scComplete && !proofreadingActive && !proofreadingOffer && !busy && !showScHandoff && (
              <div className="mt-3 rounded-md border border-stone-200 bg-[#faf9f6] px-5 py-4" data-testid="sc-forward-fallback">
                <p className="text-[13.5px] text-stone-700 mb-3">
                  There's nothing specific to coach in your sentences right now. You can keep revising {bridgeMulti ? "your writing" : "your paragraph"} above, or move on to your final review.
                </p>
                <button
                  onClick={() => roundChoose("finish")}
                  disabled={busy}
                  data-testid="sc-forward-final-review-btn"
                  className="bg-[#8C3A2A] text-white px-5 py-2.5 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
                >
                  Continue to Final Review
                </button>
              </div>
            )}

            {/* COMPOSITION → SENTENCE CRAFT TRANSITION. Composition has determined the paragraph is
                communicatively sufficient, so the screen behaves as though Composition is complete: a
                dominant, unmistakable next-step CTA replaces the revision affordance. Clicking it goes
                straight to the Sentence-Craft loading screen (beginStage — no intermediate screens). */}
            {/* TESTING BRIDGE — multi-paragraph: this part is done, move to the NEXT part (not Sentence Craft yet). */}
            {showScHandoff && bridgeHasMore && (
              <div
                data-testid="composition-next-part-transition"
                className="mt-6 rounded-md border border-[#8C3A2A]/30 bg-gradient-to-br from-[#8C3A2A]/[0.06] to-[#8C3A2A]/[0.02] p-6"
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] font-mono-panel mb-2">
                  <Check className="h-3.5 w-3.5" /> Part {bridgeCursor.index + 1} of {bridgeCursor.total} is doing its job
                </div>
                <p className="font-serif-display text-[1.4rem] leading-tight text-stone-900 mb-1.5">
                  {paragraphStanding.working}
                </p>
                <p className="text-[14px] text-stone-600 mb-5">
                  This part of your writing is communicating its ideas clearly. Next, let&rsquo;s work closely on the following part.
                </p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                  <button
                    onClick={bridgeNext}
                    disabled={busy}
                    data-testid="composition-continue-next-part"
                    className="group inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-7 py-3.5 rounded-sm font-medium tracking-wide text-[16px] shadow-sm hover:bg-[#6B2C20] enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40"
                  >
                    Continue to the next part
                    <ArrowRight className="h-4.5 w-4.5 transition-transform group-enabled:group-hover:translate-x-0.5" />
                  </button>
                  <button
                    onClick={() => setKeepRefining(true)}
                    disabled={busy}
                    data-testid="composition-keep-refining-part"
                    className="text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2 transition-colors disabled:opacity-40"
                  >
                    Keep refining this part
                  </button>
                </div>
              </div>
            )}

            {showScHandoff && !bridgeHasMore && (
              <div
                data-testid="composition-complete-transition"
                className="mt-6 rounded-md border border-[#8C3A2A]/30 bg-gradient-to-br from-[#8C3A2A]/[0.06] to-[#8C3A2A]/[0.02] p-6"
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] font-mono-panel mb-2">
                  <Check className="h-3.5 w-3.5" /> Where your {bridgeMulti ? "writing stands" : "paragraph stands"}
                </div>
                <p className="font-serif-display text-[1.4rem] leading-tight text-stone-900 mb-1.5" data-testid="standing-working">
                  {paragraphStanding.working}
                </p>
                {paragraphStanding.issues.length > 0 ? (
                  <div className="mb-5" data-testid="standing-remaining">
                    <p className="text-[13px] text-stone-600 mb-1.5">There&rsquo;s still more you could strengthen if you want to keep working:</p>
                    <ul className="space-y-1 mb-3">
                      {paragraphStanding.issues.map((it, i) => (
                        <li key={i} className="flex items-start gap-2 text-[14px] leading-relaxed text-stone-700" data-testid={`standing-issue-${i}`}>
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#8C3A2A]/50 shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[13px] text-stone-500">You&rsquo;ve done enough here to move on to <span className="font-semibold text-stone-700">Sentence Craft</span>, or keep refining first.</p>
                  </div>
                ) : (
                  <p className="text-[14px] text-stone-600 mb-5">
                    You&rsquo;ve done enough higher-order work here &mdash; your next step is <span className="font-semibold text-stone-800">Sentence Craft</span>, or you can keep refining.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                  <button
                    onClick={() => (bridgeMulti ? bridgeNext() : beginStage("Composition", "Sentence Craft", "sentence_craft"))}
                    disabled={busy}
                    data-testid="composition-continue-sentence-craft"
                    className="group inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-7 py-3.5 rounded-sm font-medium tracking-wide text-[16px] shadow-sm hover:bg-[#6B2C20] enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40"
                  >
                    Continue to Sentence Craft
                    <ArrowRight className="h-4.5 w-4.5 transition-transform group-enabled:group-hover:translate-x-0.5" />
                  </button>
                  <button
                    onClick={() => setKeepRefining(true)}
                    disabled={busy}
                    data-testid="composition-keep-refining"
                    className="text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2 transition-colors disabled:opacity-40"
                  >
                    Keep refining {bridgeMulti ? "this part" : "my paragraph"}
                  </button>
                  {scOfferPending && (
                    <button
                      onClick={() => roundChoose("finish")}
                      disabled={busy}
                      data-testid="composition-finish-review"
                      className="text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2 transition-colors disabled:opacity-40"
                    >
                      Finish & see my review
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Learner chose to keep refining after readiness — keep a persistent, obvious path forward. */}
            {(compositionReady || scOfferPending) && keepRefining && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-stone-200 bg-white px-4 py-3" data-testid="composition-refining-strip">
                <span className="text-[13px] text-stone-600">
                  {bridgeHasMore ? "Refine as much as you like — the next part is ready when you are."
                    : "Refine as much as you like — Sentence Craft is ready when you are."}
                </span>
                <button
                  onClick={() => (bridgeHasMore ? bridgeNext() : bridgeMulti ? bridgeNext() : beginStage("Composition", "Sentence Craft", "sentence_craft"))}
                  disabled={busy}
                  data-testid="composition-continue-sentence-craft-inline"
                  className="group inline-flex items-center gap-2 text-[13px] font-medium text-[#8C3A2A] border border-[#8C3A2A]/40 px-4 py-2 rounded-full hover:bg-[#8C3A2A] hover:text-white transition-colors disabled:opacity-40"
                >
                  {bridgeHasMore ? "Continue to the next part" : "Continue to Sentence Craft"}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
            )}

            {/* Scratch pad relocated to the TOP of the Writing Studio column (above the dialogue box). */}
            {/* Primary action bar moved into the LEFT writing column (adjacent to the editor). */}

            {/* PROOFREADING CLOSURE — required pass satisfied, real findings remain. Present the choice
                (keep proofreading vs continue to final review). NEVER shows the correction (§8/§17). */}
            {pfClosure && (
              <div
                data-testid="pf-closure-panel"
                className="mt-4 rounded-md border border-[#8C3A2A]/30 bg-gradient-to-br from-[#8C3A2A]/[0.06] to-[#8C3A2A]/[0.02] p-6"
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] font-mono-panel mb-2">
                  <Check className="h-3.5 w-3.5" /> Where your proofreading stands
                </div>
                <p className="font-serif-display text-[1.4rem] leading-tight text-stone-900 mb-1.5" data-testid="pf-closure-heading">
                  {bridgeMulti ? "Your writing is" : "Your paragraph is"} ready to move on to the final review.
                </p>
                {(pfClosure.remaining || []).length > 0 && (
                  <div className="mb-5" data-testid="pf-closure-remaining">
                    <p className="text-[13px] text-stone-600 mb-1.5">There are still a few proofreading items you could check if you want to keep working:</p>
                    <ul className="space-y-1">
                      {(pfClosure.remaining || []).slice(0, 3).map((it, i) => (
                        <li key={i} className="flex items-start gap-2 text-[14px] leading-relaxed text-stone-700" data-testid={`pf-closure-item-${i}`}>
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#8C3A2A]/50 shrink-0" />
                          <span>{it.label}{it.count > 1 ? ` (${it.count})` : ""}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                  <button
                    onClick={() => roundChoose("proofreading_finish")}
                    disabled={busy}
                    data-testid="pf-closure-continue-final"
                    className="group inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-7 py-3.5 rounded-sm font-medium tracking-wide text-[16px] shadow-sm hover:bg-[#6B2C20] enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40"
                  >
                    Continue to Final Review
                    <ArrowRight className="h-4.5 w-4.5 transition-transform group-enabled:group-hover:translate-x-0.5" />
                  </button>
                  <button
                    onClick={() => roundChoose("keep_proofreading")}
                    disabled={busy}
                    data-testid="pf-closure-keep-proofreading"
                    className="text-[13px] font-medium text-[#8C3A2A] border border-[#8C3A2A]/40 px-4 py-2 rounded-full hover:bg-[#8C3A2A] hover:text-white transition-colors disabled:opacity-40"
                  >
                    Keep proofreading
                  </button>
                </div>
              </div>
            )}

            {/* PROOFREADING controls — always available so the learner is never trapped: fix & resend
                (above), or leave proofreading for the Final Review at any time. */}
            {proofreadingActive && !pfClosure && (
              <div className="mt-3 flex flex-wrap items-center justify-end gap-2" data-testid="proofreading-controls">
                <button
                  onClick={() => roundChoose("pf_dismiss")}
                  disabled={busy}
                  data-testid="proofreading-dismiss-btn"
                  className="text-[12px] border border-stone-300 text-stone-700 px-3 py-1.5 rounded-full hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                >
                  This is intentional
                </button>
                <button
                  onClick={() => roundChoose("skip_proofreading")}
                  disabled={busy}
                  data-testid="proofreading-active-skip-btn"
                  className="text-[12px] border border-stone-300 text-stone-700 px-3 py-1.5 rounded-full hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                >
                  Skip remaining proofreading
                </button>
                <button
                  onClick={() => roundChoose("skip_proofreading")}
                  disabled={busy}
                  data-testid="proofreading-final-review-btn"
                  className="text-[12px] bg-[#8C3A2A] text-white px-3 py-1.5 rounded-full hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
                >
                  Go to Final Review
                </button>
              </div>
            )}

            {/* Talk-to-The Writing Studio consolidated into the persistent The Writing Studio conversation above. */}

            {/* LEARNER CONTROL (§4) — moved to the BOTTOM of the composition workspace. The learner decides
                whether to move on to Sentence Craft, jump to Proofreading, or finish. Ending is never
                framed as failure; it routes to the grounded review. Available throughout Round-1 writing. */}
            {episode?.active_round === 1 && !proofreadingActive && ["WORKING", "CLOSING"].includes(episode.round1?.state) && !scOfferPending && (
              bridgeMulti ? (
                // TESTING BRIDGE — while the student's writing has several parts, the ONLY forward control
                // routes through the paragraph cursor (bridgeNext). This both prevents the earlier bug of
                // dropping parts 2..N and gives a reliable learner-driven way to move on when ready.
                <div className="mt-6" data-testid="round1-controls">
                  <span className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400">
                    What next
                  </span>
                  <div className="mt-2 flex items-center gap-2 flex-nowrap">
                    <button
                      onClick={bridgeNext}
                      disabled={busy}
                      data-testid="round1-control-next-part"
                      className="whitespace-nowrap text-[12px] border border-stone-300 text-stone-700 px-3 py-1.5 rounded-full hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                    >
                      {bridgeHasMore ? "Move on to the next part" : "Continue to Sentence Craft"}
                    </button>
                  </div>
                </div>
              ) : r1ControlDismissed ? (
                <button
                  onClick={() => roundChoose("finish")}
                  disabled={busy}
                  data-testid="round1-finish-inline"
                  className="mt-6 text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2 transition-colors disabled:opacity-40"
                >
                  Finish &amp; see my review
                </button>
              ) : (
                <div className="mt-6" data-testid="round1-controls">
                  <span className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400">
                    What next
                  </span>
                  <div className="mt-2 flex items-center gap-2 flex-nowrap">
                    <button
                      onClick={() => beginStage("Composition", "Sentence Craft", "sentence_craft")}
                      disabled={busy}
                      data-testid="round1-control-sentence-craft"
                      className="whitespace-nowrap text-[12px] border border-stone-300 text-stone-700 px-3 py-1.5 rounded-full hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                    >
                      Sentence Craft
                    </button>
                    <button
                      onClick={() => beginStage("Composition", "Proofreading", "proofreading")}
                      disabled={busy}
                      data-testid="round1-control-proofreading"
                      className="whitespace-nowrap text-[12px] border border-stone-300 text-stone-700 px-3 py-1.5 rounded-full hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                    >
                      Skip to Proofreading
                    </button>
                    <button
                      onClick={() => roundChoose("finish")}
                      disabled={busy}
                      data-testid="round1-control-finish"
                      className="whitespace-nowrap text-[12px] border border-stone-300 text-stone-700 px-3 py-1.5 rounded-full hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                    >
                      Finish
                    </button>
                  </div>
                </div>
              )
            )}
              </div>{/* end LEFT writing column */}

              {/* RIGHT — persistent COMPASS dialogue / coaching (primary instructional medium). On desktop
                  it is a STICKY, viewport-bounded chat viewport: it stays beside the writing area and its
                  conversation scrolls internally, so the document never scrolls to reveal new responses. */}
              <div className="min-w-0 flex flex-col lg:sticky lg:top-6 lg:self-start lg:h-[calc(100vh-3rem)]" data-testid="compass-column">

            {/* Scratch pad relocated to an icon toggle in the action bar (under the text). */}

            {/* ONE PERSISTENT COMPASS CONVERSATION — engine coaching + Ask-The Writing Studio dialogue in a single
                internally-scrolling thread, so accumulating turns never push the workspace down the page. */}
            {!viewingPast && (
            <div ref={convBoxRef} className="flex flex-col min-h-0 rounded-md border border-[#8C3A2A]/25 bg-white max-h-[68vh] lg:max-h-none lg:h-full relative" data-testid="compass-conversation">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-stone-100">
                <MessageSquareQuote className="h-4 w-4 shrink-0 text-[#8C3A2A]" />
                <div className="min-w-0" data-testid="ask-compass-invite">
                  <span className="font-mono-panel text-[11px] uppercase tracking-[0.18em] text-[#8C3A2A]">The Writing Studio</span>
                  <span className="ml-2 text-[11px] text-stone-400">Ask a question at any time.</span>
                </div>
                {scActive && (
                  <span className="ml-auto shrink-0 font-mono-panel normal-case text-[10px] text-stone-400" data-testid="conversation-sc-target">
                    {sc?.focus_label ? sc.focus_label : "Sentence Craft"}
                    {sc?.sentence_count ? ` \u00b7 ${(sc.active_sentence_index ?? 0) + 1}/${sc.sentence_count}` : ""}
                  </span>
                )}
              </div>
              <div ref={convScrollRef} onScroll={onConvScroll} className="flex-1 min-h-0 overflow-y-auto custom-scroll px-4 py-4 space-y-3" data-testid="conversation-thread">
                {conversation.length === 0 && !streamingCoaching && !busy && (
                  <p className="text-[13px] text-stone-400" data-testid="conversation-empty">Your coaching will appear here as a conversation.</p>
                )}
                {conversation.map((m) => (
                  m.who === "student" ? (
                    <div key={m.id} className="flex justify-end">
                      <div data-testid="conversation-student" className="max-w-[85%] bg-stone-900 text-white text-[14px] leading-relaxed rounded-lg rounded-br-sm px-3.5 py-2 whitespace-pre-wrap">
                        {m.text}
                      </div>
                    </div>
                  ) : (
                    <div key={m.id}>
                      {m.id === newCompassId && (
                        <div className="flex items-center gap-2 my-2" data-testid="conversation-new-divider">
                          <span className="h-px flex-1 bg-[#8C3A2A]/25" />
                          <span className="text-[9px] font-mono-panel uppercase tracking-[0.18em] text-[#8C3A2A] bg-[#8C3A2A]/10 rounded-full px-2 py-0.5">New response</span>
                          <span className="h-px flex-1 bg-[#8C3A2A]/25" />
                        </div>
                      )}
                      <div className="flex justify-start">
                        <div
                          data-testid="conversation-compass"
                          className={"max-w-[94%] bg-[#faf9f6] border text-stone-800 text-[15px] font-serif-display leading-relaxed rounded-lg rounded-bl-sm px-3.5 py-2.5 whitespace-pre-wrap transition-shadow duration-500 " + (m.id === newCompassId ? "border-[#8C3A2A]/40 border-l-2 border-l-[#8C3A2A] ring-2 ring-[#8C3A2A]/25 shadow-sm" : "border-stone-200 border-l-2 border-l-[#8C3A2A]")}
                        >
                          {renderCoachingRich(m.text)}
                        </div>
                      </div>
                    </div>
                  )
                ))}
                {streamingCoaching && !scComplete && (
                  <div className="flex justify-start" data-testid="preview-coaching-streaming">
                    <div className="max-w-[94%] bg-[#faf9f6] border border-stone-200 border-l-2 border-l-[#8C3A2A] text-stone-800 text-[15px] font-serif-display leading-relaxed rounded-lg rounded-bl-sm px-3.5 py-2.5 whitespace-pre-wrap">
                      {renderCoachingRich(streamingCoaching.content)}
                      <span className="inline-block w-[2px] h-[1.1em] align-[-0.15em] ml-0.5 bg-[#8C3A2A] animate-pulse" aria-hidden />
                    </div>
                  </div>
                )}
                {busy && !streamingCoaching && (
                  <div className="flex justify-start" data-testid="preview-thinking">
                    <div className="max-w-[94%] w-full space-y-3">
                      <EvaluatingStatus ackLabel={pendingAck} focusLabel={evalFocus.label} checkLine={evalFocus.check} firstTurn={isFirstMoment} submitAt={submitAtRef.current} />
                      {noticing && (noticing.observations || []).length > 0 ? <ThinkingWith /> : <Thinking />}
                    </div>
                  </div>
                )}
                {lastTurnFailed && !busy && (
                  <div className="flex justify-start" data-testid="turn-interrupted-notice">
                    <div className="max-w-[94%] rounded-lg border border-amber-300/70 bg-amber-50 px-3.5 py-3">
                      <p className="text-[14px] text-stone-700 mb-2.5" data-testid="turn-interrupted-text">{lastAiTurn?.content || "That response was interrupted before it finished. Your writing is saved."}</p>
                      {!lastAiTurn?.content && <button
                        onClick={retryLastTurn}
                        disabled={busy}
                        data-testid="turn-retry-btn"
                        className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white px-4 py-1.5 rounded-full text-[13px] font-medium hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
                      >
                        Try again
                      </button>}
                    </div>
                  </div>
                )}
              </div>
              {showJump && (
                <button
                  onClick={scrollConvToBottom}
                  data-testid="coach-feedback-toast"
                  role="status"
                  aria-live="polite"
                  className="absolute left-1/2 -translate-x-1/2 bottom-[76px] z-10 inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-4 py-2 rounded-full shadow-lg text-[12.5px] font-medium hover:bg-[#6B2C20] transition-colors"
                >
                  New response
                  <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
                </button>
              )}
              {!scComplete && (
              <div className="border-t border-stone-100 p-3" data-testid="ask-compass-panel">
                <div className="flex items-end gap-2">
                  <textarea
                    data-testid="ask-compass-input"
                    value={askMsg}
                    onChange={(e) => setAskMsg(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendDialogue(); }}
                    rows={2}
                    disabled={busy}
                    placeholder=""
                    className="flex-1 bg-[#faf9f6] border border-stone-300 rounded-sm p-2.5 text-sm text-stone-900 placeholder:text-stone-400 outline-none focus:ring-1 focus:ring-[#8C3A2A] resize-none disabled:opacity-50"
                  />
                  <button
                    onClick={sendDialogue}
                    data-testid="ask-compass-send"
                    disabled={!askMsg.trim() || busy}
                    className="shrink-0 inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] px-3.5 py-2.5 rounded-sm hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareQuote className="h-4 w-4" />}
                    Send
                  </button>
                </div>
                {activeCoaching && !busy && (
                  <button
                    onClick={sendExplain}
                    data-testid="preview-explain-more"
                    className="mt-2 text-[11px] font-mono-panel uppercase tracking-[0.14em] text-stone-500 hover:text-[#8C3A2A] transition-colors"
                  >
                    Explain this another way
                  </button>
                )}
              </div>
              )}
            </div>
            )}

            {/* Structural diagram moved to the LEFT column (under the action-bar icons). */}

              </div>{/* end RIGHT compass column */}
            </div>{/* end two-pane grid */}

            {/* HYPIW / Sentence-Craft map relocated to the LEFT column (under the action-bar icons). */}
          </div>
        )}
      </main>

      {/* FOCUS box — a MOVABLE, closable panel (starts open on each new sentence) that isolates the
          target sentence + its instructions. No backdrop, so the writing box stays fully usable. Edits
          splice into the SAME draft, so the corrected sentence appears in the text box immediately. */}
      {focusPassageOpen && focusAvailable && !showScHandoff && (
        <div
          data-testid="focus-passage-modal"
          className="fixed z-50 w-[min(92vw,460px)] max-w-[calc(100vw-16px)] rounded-lg bg-white shadow-2xl ring-1 ring-[#8C3A2A]/25"
          style={{
            // CONTAINMENT: never past the right edge — at 390px (or after a drag / resize) the panel stays on screen.
            left: `max(8px, min(${focusPos ? focusPos.x : (typeof window !== "undefined" ? Math.max(24, window.innerWidth / 2 - 230) : 380)}px, calc(100vw - min(92vw, 460px) - 8px)))`,
            top: focusPos ? focusPos.y : 130,
          }}
        >
          <div
            onMouseDown={startFocusDrag}
            className="flex items-center justify-between px-4 py-2.5 border-b border-stone-100 cursor-move select-none bg-[#8C3A2A]/[0.06] rounded-t-lg"
            data-testid="focus-passage-drag"
          >
            <span className="flex items-center gap-1.5 font-mono-panel text-[10px] uppercase tracking-[0.16em] text-[#8C3A2A]">
              <GripVertical className="h-3.5 w-3.5" /> {focusTitle}
            </span>
            <button onClick={() => setFocusPassageOpen(false)} data-testid="focus-passage-close" className="text-stone-400 hover:text-stone-700">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="px-4 py-3">
            {focusInstruction && (
              <p className="mb-3 rounded-md bg-[#faf9f6] border border-stone-200 px-3 py-2 text-[13px] leading-relaxed text-stone-700 font-serif-display" data-testid="focus-passage-instructions">
                {renderCoaching(focusInstruction)}
              </p>
            )}
            <textarea
              data-testid="focus-passage-input"
              value={focusValue}
              onChange={(e) => focusOnChange(e.target.value)}
              onPaste={capturePaste}
              onDrop={blockDrop}
              disabled={!focusEditable || busy}
              rows={focusScoped ? 3 : 5}
              className="block w-full rounded-md border border-[#8C3A2A]/30 bg-white px-3 py-2.5 text-[17px] leading-8 text-stone-900 outline-none focus:ring-1 focus:ring-[#8C3A2A] resize-none font-serif-display"
            />
            <p className="mt-1 text-[11px] text-stone-400">Editing here updates your paragraph in the text box.</p>
          </div>
          <div className="flex items-center justify-end gap-3 px-4 pb-3">
            <button onClick={() => setFocusPassageOpen(false)} data-testid="focus-passage-done" className="text-[13px] text-stone-600 hover:text-[#8C3A2A] px-3 py-1.5">
              Close
            </button>
            <button
              onClick={() => { setFocusPassageOpen(false); sendRevision(); }}
              disabled={!draft.trim() || busy || !dirty}
              data-testid="focus-passage-send"
              className="inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-4 py-2 rounded-sm font-medium hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
            >
              Send revision <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {experienceActive && !earlyExitOpen && session?.id && (
        <InContextFeedback sessionId={session.id} getContext={getFeedbackContext} />
      )}
      {feedbackOpen && session?.id && (
        <FeedbackModal sessionId={session.id} onClose={() => setFeedbackOpen(false)} />
      )}
      {earlyExitOpen && (
        <EarlyExitModal
          sessionId={session?.id}
          onLeave={() => { setEarlyExitOpen(false); restart(); }}
        />
      )}
      <AuthModal
        open={savePromptOpen}
        onOpenChange={setSavePromptOpen}
        reason="Create a free account to save this paragraph and review — and pick up where you left off next time."
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
}

const HELP_STARTERS = [
  "Explain why…",
  "Compare…",
  "What do you think about…",
  "How would you apply…",
  "What caused…",
  "What might happen if…",
  "Describe the relationship between…",
  "Use evidence to support…",
];

// Chapter 4 — Assignment Screen. The educator creates a brief, authentic
// assignment in their own words. No subject/grade/standard/type is required; no
// evaluation, rewriting, or analysis happens here; no session is created yet.
function AssignmentScreen({ assignment, setAssignment, onContinue, onBack }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const meaningful = assignment.trim().length >= 10;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="flex-1 flex flex-col justify-center max-w-xl mx-auto w-full py-10"
      data-testid="assignment-screen"
    >
      <h1 className="font-serif-display text-3xl sm:text-4xl leading-snug text-stone-900">
        Create a brief assignment
      </h1>
      <p className="text-stone-600 mt-4 text-[15px] leading-relaxed">
        Think of an assignment you might genuinely give to a student. Enter a prompt that could be
        answered in one thoughtful paragraph. It might ask a learner to explain, interpret, compare,
        argue, reflect, or apply an idea.
      </p>

      <label
        htmlFor="assignment-input"
        className="mt-7 block font-mono-panel text-[11px] uppercase tracking-[0.14em] text-stone-500 mb-1.5"
      >
        Your assignment
      </label>
      <textarea
        id="assignment-input"
        data-testid="assignment-input"
        value={assignment}
        onChange={(e) => setAssignment(e.target.value)}
        rows={5}
        autoFocus
        placeholder="For example: Explain why a character made an important decision, compare two approaches to solving a problem, or describe how a concept applies to a real situation."
        className="w-full bg-white border border-stone-300 rounded-sm p-5 text-[16px] leading-8 text-stone-900 placeholder:text-stone-400 outline-none focus:ring-1 focus:ring-stone-900 focus:border-stone-900 transition-colors resize-none"
      />
      <p className="mt-2 text-[13px] text-stone-500">
        Keep the assignment brief. You will respond to it yourself in the next step.
      </p>
      <p className="mt-1 text-[12px] text-stone-400" data-testid="assignment-privacy-note">
        Please do not include a student's name or other identifying information.
      </p>

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setHelpOpen((v) => !v)}
          data-testid="assignment-help-toggle"
          aria-expanded={helpOpen}
          className="text-[12px] font-mono-panel uppercase tracking-[0.14em] text-stone-500 hover:text-[#8C3A2A] transition-colors"
        >
          Help me create one
        </button>
        {helpOpen && (
          <div
            data-testid="assignment-help-area"
            className="mt-3 bg-[#faf9f6] border border-stone-200 rounded-sm p-4"
          >
            <p className="text-[13px] text-stone-600 mb-2">Try beginning with one of these:</p>
            <ul className="space-y-1">
              {HELP_STARTERS.map((s) => (
                <li key={s} className="text-[14px] text-stone-700 font-serif-display">• {s}</li>
              ))}
            </ul>
            <p className="text-[13px] text-stone-600 mt-3">
              Choose one beginning and complete it in your own words.
            </p>
          </div>
        )}
      </div>

      <div className="mt-7 flex items-center gap-5">
        <button
          onClick={onContinue}
          data-testid="assignment-continue-button"
          disabled={!meaningful}
          className="group inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-7 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue
          <ArrowRight className="h-4 w-4 transition-transform group-enabled:group-hover:translate-x-0.5" />
        </button>
        <button
          type="button"
          onClick={onBack}
          data-testid="assignment-back-button"
          className="text-[12px] font-mono-panel uppercase tracking-[0.14em] text-stone-400 hover:text-stone-700 transition-colors"
        >
          Back
        </button>
      </div>
      {!meaningful && (
        <span className="text-stone-400 text-[13px] mt-2" data-testid="assignment-hint">
          Please enter the assignment you would like to respond to.
        </span>
      )}
    </motion.div>
  );
}

// Chapter 5 — Writing Screen. The educator responds, as a learner, to their
// authentic assignment with one genuine first-draft paragraph. The assignment
// is shown read-only; no live AI/grammar/autocomplete assistance appears; the
// exact response is preserved. Submit hands off to the existing thinking state.
function WritingScreen({ ot, response, setResponse, onSubmit, onBack, submitting, onEditCapture, onPasteCapture, onDropCapture, directCompose = false, hidePlanning = false }) {
  const meaningful = response.trim().length >= 15;
  const hasOt = !!ot;
  // Full-path (FSC → Composition) users already have their draft in the box; the legacy
  // "From your planning" panel is obsolete clutter for them, so it is suppressed.
  const showPlanning = hasOt && !hidePlanning;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`flex-1 flex flex-col w-full py-10 ${showPlanning ? "max-w-5xl" : "max-w-xl mx-auto justify-center"}`}
      data-testid="writing-screen"
    >
      {/* Persistent COMPOSITION orientation — marks the shared convergence point for BOTH the full
          pathway and Composition-Only. Restrained; does not redesign the writing box. */}
      <div data-testid="composition-orientation" className="mb-6 border-l-2 border-[#8C3A2A] pl-3">
        <p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A]">Composition</p>
        <p className="text-[13px] text-stone-500 mt-0.5">Develop your writing for your reader</p>
      </div>
      <div className={showPlanning ? "grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] gap-6 lg:gap-10" : ""}>
        <div className="min-w-0 flex flex-col">
      <h1 className="font-serif-display text-3xl sm:text-4xl leading-snug text-stone-900">
        {directCompose ? "Begin your draft" : "Write your response"}
      </h1>
      <p className="text-stone-600 mt-4 text-[15px] leading-relaxed">
        {directCompose
          ? "Type your draft below, then use The Writing Studio to develop it for your reader. Write a genuine draft — you don't have to make it perfect before The Writing Studio sees it."
          : "Respond to your assignment in one thoughtful paragraph. Write a genuine first draft. Do not try to make it perfect before The Writing Studio sees it."}
      </p>

      <label
        htmlFor="writing-input"
        className="mt-6 block font-mono-panel text-[11px] uppercase tracking-[0.14em] text-stone-500 mb-1.5"
      >
        Your first draft
      </label>
      <textarea
        id="writing-input"
        data-testid="writing-input"
        value={response}
        onChange={(e) => { onEditCapture?.(); setResponse(e.target.value); }}
        onPaste={(e) => onPasteCapture?.(e)}
        onDrop={(e) => onDropCapture?.(e)}
        rows={9}
        autoFocus
        placeholder="Write or enter your response to the assignment."
        className="w-full bg-white border border-stone-300 rounded-sm p-5 text-[16px] leading-8 text-stone-900 placeholder:text-stone-400 outline-none focus:ring-1 focus:ring-stone-900 focus:border-stone-900 transition-colors resize-y min-h-[220px]"
      />
      <p className="mt-2 text-[13px] text-stone-500">
        When you've written enough for The Writing Studio to work with, ask it to read your writing.
      </p>

      <div className="mt-7 flex items-center gap-5">
        <button
          onClick={onSubmit}
          data-testid="writing-submit-button"
          disabled={!meaningful || submitting}
          className="group inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-7 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              Share with The Writing Studio
              <ArrowRight className="h-4 w-4 transition-transform group-enabled:group-hover:translate-x-0.5" />
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onBack}
          data-testid="writing-back-button"
          className="text-[12px] font-mono-panel uppercase tracking-[0.14em] text-stone-400 hover:text-stone-700 transition-colors"
        >
          Back to assignment
        </button>
      </div>
      {!meaningful && (
        <span className="text-stone-400 text-[13px] mt-2" data-testid="writing-hint">
          Please write enough for The Writing Studio to understand the idea you are trying to express.
        </span>
      )}
        </div>
        {showPlanning && (
          <OTThinkingPanel
            ot={ot}
            only={["__original", "the_assignment", "my_current_answer", "my_plan"]}
            title="From your planning"
          />
        )}
      </div>
    </motion.div>
  );
}

// The wait period is now instructional. Instead of a generic loading state, it
// models how experienced writers think (writer metacognition), then bridges to
// The Writing Studio's instructional focus. It NEVER narrates The Writing Studio's internal reasoning.
function MetacognitionCue({ testid, toFocus }) {
  const seqRef = useRef(null);
  if (seqRef.current === null) {
    seqRef.current = metacognitionSequence({ toFocus, general: toFocus ? 3 : 4 });
  }
  const seq = seqRef.current;
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      // When bridging to focus, hold on the final focus line; otherwise cycle.
      setI((n) => (toFocus ? Math.min(n + 1, seq.length - 1) : (n + 1) % seq.length));
    }, 4800);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seq.length, toFocus]);
  const isFocus = toFocus && i === seq.length - 1;
  return (
    <div className="flex items-start gap-3 pl-1" data-testid={testid}>
      <div className="flex items-center gap-1.5 mt-2">
        <span className="thinking-dot h-2 w-2 rounded-full bg-[#8C3A2A]" />
        <span className="thinking-dot h-2 w-2 rounded-full bg-[#8C3A2A]" style={{ animationDelay: "0.2s" }} />
        <span className="thinking-dot h-2 w-2 rounded-full bg-[#8C3A2A]" style={{ animationDelay: "0.4s" }} />
      </div>
      <motion.p
        key={i}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: isFocus ? 0.9 : 0.72, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        data-testid={`${testid}-line`}
        className={`text-sm leading-relaxed font-serif-display max-w-md ${isFocus ? "text-[#8C3A2A] italic" : "text-stone-600"}`}
      >
        {seq[i]}
      </motion.p>
    </div>
  );
}

// ZERO-ISSUE SELF-REVIEW — a lightweight, learner-controlled proofreading pass shown when The Writing Studio
// found no mechanical issues. NO AI here: the learner inspects/edits their own paragraph against a
// short checklist and decides when they are done. Reuses the completion-screen layout language.
function SelfReviewScreen({ draft, onChange, busy, onDone }) {
  const CHECKLIST = [
    ["Spelling", "Every word is spelled the way you mean it."],
    ["Punctuation", "Periods, commas, and question marks land where they belong."],
    ["Capitalization", "Sentences and proper nouns start with a capital letter."],
    ["Apostrophes", "Contractions and possessives use apostrophes correctly."],
    ["Agreement", "Subjects and verbs match (is/are, was/were)."],
    ["Repeated words", "No accidental doubled words (\u201cthe the\u201d)."],
  ];
  const words = (draft || "").trim() ? (draft || "").trim().split(/\s+/).length : 0;
  return (
    <div className="flex-1 flex flex-col py-10 max-w-3xl mx-auto min-h-[60vh]" data-testid="self-review-screen">
      <StageStepper current="sentence_craft" />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="mt-10"
      >
        <div className="text-center">
          <div
            className="inline-flex items-center gap-2 rounded-full bg-stone-100 text-stone-600 px-3 py-1 text-[11px] font-mono-panel uppercase tracking-[0.16em]"
            data-testid="self-review-badge"
          >
            <PenLine className="h-3.5 w-3.5" /> Review My Writing
          </div>
          <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl text-stone-900" data-testid="self-review-title">
            Your Own Review
          </h1>
          <p className="mt-4 text-[16px] text-stone-700 leading-relaxed max-w-[52ch] mx-auto" data-testid="self-review-message">
            I didn't find any mechanics to fix, so this one's yours. Read your paragraph slowly and
            fix anything you notice. Use the checklist if it helps — then move on when you're ready.
          </p>
        </div>

        <div className="mt-8 grid md:grid-cols-[1.5fr_1fr] gap-6">
          <div>
            <label className="font-mono-panel text-[11px] uppercase tracking-[0.18em] text-stone-400">
              Your paragraph
            </label>
            <textarea
              value={draft}
              onChange={(e) => onChange(e.target.value)}
              onPaste={(e) => { if (!getTestMode()) e.preventDefault(); }}
              onDrop={(e) => { if (!getTestMode()) e.preventDefault(); }}
              data-testid="self-review-textarea"
              spellCheck
              className="mt-2 w-full h-64 resize-none rounded-sm border border-stone-300 bg-white px-4 py-3 text-[16px] leading-relaxed text-stone-800 font-serif-display focus:border-[#8C3A2A] focus:outline-none custom-scroll"
            />
            <p className="mt-1 text-[12px] text-stone-400" data-testid="self-review-wordcount">{words} words</p>
          </div>
          <div className="rounded-sm border border-stone-200 bg-stone-50/60 px-4 py-4" data-testid="self-review-checklist">
            <p className="font-mono-panel text-[11px] uppercase tracking-[0.18em] text-stone-400 mb-3">
              Proofreading checklist
            </p>
            <ul className="space-y-3">
              {CHECKLIST.map(([label, hint]) => (
                <li key={label} className="flex gap-2.5" data-testid={`self-review-check-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Check className="h-4 w-4 mt-0.5 text-[#8C3A2A] shrink-0" />
                  <span className="text-[13.5px] text-stone-700 leading-snug">
                    <span className="font-medium text-stone-800">{label}.</span> {hint}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            onClick={onDone}
            disabled={busy}
            data-testid="self-review-done-btn"
            className="w-full sm:w-auto bg-[#8C3A2A] text-white px-8 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
          >
            I'm Finished Reviewing
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// SENTENCE CRAFT completion — dedicated FOCAL screen (explicit completion event, not a box buried in
// the workspace). Shows the completion, then the two forward choices as the primary interaction.
function SCCompleteScreen({ categories, noIssues, remaining, busy, onKeepGoing, onProofread, onSelfReview, onSkip }) {
  const cats = (categories || []).filter(Boolean);
  const hasProofread = cats.length > 0;
  const rem = (remaining || []).filter(Boolean);
  const hasMore = rem.length > 0;
  const zeroIssueOffer = !!noIssues && !hasProofread;
  return (
    <div className="flex-1 flex flex-col justify-center py-10 max-w-2xl mx-auto min-h-[60vh]" data-testid="sc-complete-screen">
      <StageStepper current="sentence_craft" />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="mt-10 text-center"
      >
        <div
          className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-[11px] font-mono-panel uppercase tracking-[0.16em]"
          data-testid="sc-complete-badge"
        >
          <Check className="h-3.5 w-3.5" /> {hasMore ? "Where your sentences stand" : "Sentence Craft"}
        </div>
        <h1
          className="mt-5 font-serif-display text-4xl sm:text-5xl text-stone-900"
          data-testid="sc-complete-title"
        >
          {hasMore ? "Where your sentences stand" : "Sentence Craft Complete"}
        </h1>
        <p className="mt-4 text-[17px] text-stone-700 leading-relaxed" data-testid="sc-complete-message">
          {hasMore
            ? "Your sentences are clear enough to move on. There are still a few things you could strengthen if you want to keep working."
            : "Your ideas and sentences are communicating well."}
        </p>
        {hasMore && (
          <p className="mt-3 text-[14px] text-stone-500" data-testid="sc-complete-remaining-note">
            Still worth a look: {rem.slice(0, 3).join("; ")}.
          </p>
        )}
        {hasProofread && (
          <p className="mt-3 text-[14px] text-stone-500" data-testid="sc-complete-proofread-note">
            Before your final review, a few mechanical things are worth tidying:{" "}
            {cats.map((c) => `${c.label.toLowerCase()} (${c.count})`).join(", ")}.
          </p>
        )}
        {zeroIssueOffer && (
          <p className="mt-3 text-[14px] text-stone-500" data-testid="sc-complete-no-issues-note">
            I didn't find any proofreading issues in your draft.
          </p>
        )}
        <p
          className="mt-9 font-mono-panel text-[11px] uppercase tracking-[0.18em] text-stone-400"
          data-testid="sc-complete-prompt"
        >
          What would you like to do next?
        </p>
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
          {hasMore && (
            <button
              onClick={onKeepGoing}
              disabled={busy}
              data-testid="sc-complete-keep-going-btn"
              className="w-full sm:w-auto bg-[#8C3A2A] text-white px-6 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
            >
              Keep refining my sentences
            </button>
          )}
          {hasProofread && (
            <button
              onClick={onProofread}
              disabled={busy}
              data-testid="sc-complete-proofread-btn"
              className="w-full sm:w-auto bg-[#8C3A2A] text-white px-6 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
            >
              Continue to Proofreading
            </button>
          )}
          {zeroIssueOffer && (
            <button
              onClick={onSelfReview}
              disabled={busy}
              data-testid="sc-complete-self-review-btn"
              className="w-full sm:w-auto bg-[#8C3A2A] text-white px-6 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
            >
              Review My Writing Myself
            </button>
          )}
          <button
            onClick={onSkip}
            disabled={busy}
            data-testid="sc-complete-skip-btn"
            className={
              hasProofread || zeroIssueOffer
                ? "w-full sm:w-auto border border-stone-300 text-stone-700 px-6 py-3 rounded-sm font-medium hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-40"
                : "w-full sm:w-auto bg-[#8C3A2A] text-white px-6 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
            }
          >
            {hasProofread
              ? "Skip Proofreading and Go to Final Review"
              : zeroIssueOffer
                ? "Continue to Final Review"
                : "Go to Final Review"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// PERCEIVED-LATENCY reframe: turn a deterministic reader-model gap (what the reader does NOT yet
// understand) into an honest "Checking whether…" line. Prefix/whole-sentence maps mirror the backend
// _VM_READER_GAP templates exactly; unknown text falls back to showing the real gap verbatim.
function reframeStillNeeds(sn) {
  if (!sn) return "";
  const prefixes = [
    ["The reader does not yet understand ", "Checking whether the reader can now understand "],
    ["The reader hasn\u2019t yet seen a concrete example of ", "Checking whether the reader can now see a concrete example of "],
    ["The reader can\u2019t yet tell what you\u2019re claiming about ", "Checking whether it\u2019s now clear what you\u2019re claiming about "],
    ["The reader doesn\u2019t yet see what your point ultimately means for ", "Checking whether it\u2019s now clear what your point ultimately means for "],
    ["The reader still needs to understand ", "Checking whether the reader can now understand "],
  ];
  for (const [pre, repl] of prefixes) {
    if (sn.startsWith(pre)) return repl + sn.slice(pre.length).replace(/\.$/, "") + "\u2026";
  }
  const whole = {
    "The reader can see your point, but not yet the reasoning behind it.": "Checking whether the reasoning behind your point now comes through\u2026",
    "The reader would grasp this more firmly with one concrete example.": "Checking whether a concrete example now grounds this\u2026",
    "The reader can\u2019t yet tell what your main point is.": "Checking whether your main point now comes through clearly\u2026",
    "The reader doesn\u2019t yet know what this paragraph is about.": "Checking whether it\u2019s now clear what this paragraph is about\u2026",
    "The reader doesn\u2019t yet see what your point ultimately means.": "Checking whether it\u2019s now clear what your point ultimately means\u2026",
    "The reader is still waiting to learn your main point.": "Checking whether your main point now comes through\u2026",
  };
  if (whole[sn]) return whole[sn];
  return "Checking whether your revision addresses this: " + sn.replace(/\.$/, "") + "\u2026";
}

// PERCEIVED-LATENCY status card: an immediate, grounded acknowledgement of the submission + the REAL
// instructional focus + the REAL question being evaluated. Makes the wait feel like shared inquiry
// ("we are examining this together") rather than a dead spinner. Nothing here is fabricated.
function EvaluatingStatus({ ackLabel, focusLabel, checkLine, firstTurn, submitAt }) {
  const FIRST_MSGS = [
    "Reading your paragraph as a whole\u2026",
    "Working out what your reader already understands\u2026",
    "Identifying the most useful thing to work on next\u2026",
  ];
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!firstTurn) return undefined;
    const start = submitAt || Date.now();
    setElapsed(Date.now() - start);
    const id = setInterval(() => setElapsed(Date.now() - start), 1000);
    return () => clearInterval(id);
  }, [firstTurn, submitAt]);
  const overLong = firstTurn && elapsed >= 45000;
  const idx = elapsed >= 24000 ? 2 : elapsed >= 12000 ? 1 : 0;
  const headline = firstTurn
    ? (overLong ? "Still working \u2014 some paragraphs take a little longer to analyze." : FIRST_MSGS[idx])
    : "Reading your revision\u2026";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      data-testid="evaluating-status"
      className="rounded-sm border border-stone-200 bg-white/80 px-4 py-3"
    >
      <div
        data-testid="evaluating-headline"
        className="text-[14px] font-semibold text-[#8C3A2A] font-serif-display leading-snug"
      >
        {headline}
      </div>
      {firstTurn && !overLong && (
        <p
          data-testid="evaluating-first-expectation"
          className="mt-1 text-[13px] leading-relaxed text-stone-500"
        >
          The Writing Studio is looking at how your main point, supporting ideas, and the reader&rsquo;s needs work
          together. A first review can take about 30&ndash;45 seconds.
        </p>
      )}
      {ackLabel && (
        <div
          data-testid="evaluating-ack"
          className="mt-3 flex items-center gap-2 text-[13px] font-medium text-emerald-700"
        >
          <Check className="h-4 w-4 shrink-0" />
          <span>{ackLabel}</span>
        </div>
      )}
      {focusLabel && (
        <div className="mt-2 flex items-baseline gap-2" data-testid="evaluating-focus">
          <span className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400 shrink-0">
            Current focus
          </span>
          <span className="text-[14px] font-semibold text-[#8C3A2A] font-serif-display leading-snug">
            {focusLabel}
          </span>
        </div>
      )}
      {checkLine && (
        <p
          data-testid="evaluating-check"
          className="mt-1.5 text-[14px] italic leading-relaxed text-stone-600 font-serif-display"
        >
          {checkLine}
        </p>
      )}
    </motion.div>
  );
}

// Shown when interim observations have NOT arrived yet: pure writer metacognition
// (keeps cycling until the acknowledgement or coaching appears).
function Thinking() {  return <MetacognitionCue testid="preview-thinking-cue" toFocus={false} />;
}

// Chapter 6 — shown AFTER the grounded acknowledgement (the interim observations).
// Progresses from writer metacognition toward The Writing Studio's instructional focus while
// the frozen engine finishes. Never narrates The Writing Studio's internal reasoning.
function ThinkingWith() {
  return <MetacognitionCue testid="preview-thinking-with" toFocus={true} />;
}

// The interim observations. Grounded observation 1 (emerging thesis) →
// (pause) → grounded observation 2 (the structural relationship/distinction),
// if present. Same voice and surface as the coaching that follows, so the
// encounter reads as one continuous, developing line of instruction.
function NoticingBeats({ noticing, revealStage }) {
  const observations = (noticing && noticing.observations) || [];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="preview-noticing"
      className="mt-5 bg-white border-l-2 border-[#8C3A2A] border-y border-r border-stone-200 rounded-sm p-5"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] font-mono-panel mb-3">
        <MessageSquareQuote className="h-3.5 w-3.5" />
        Your coach
      </div>
      <div className="space-y-3">
        {observations[0] && (
          <Beat show={revealStage >= 1} testid="preview-noticing-observation-1">
            {observations[0]}
          </Beat>
        )}
        {observations[1] && (
          <Beat show={revealStage >= 2} testid="preview-noticing-observation-2">
            {observations[1]}
          </Beat>
        )}
      </div>
    </motion.div>
  );
}

function Beat({ show, testid, children }) {
  if (!show) return null;
  return (
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      data-testid={testid}
      className="text-stone-800 leading-relaxed text-[16px] font-serif-display"
    >
      {children}
    </motion.p>
  );
}
